import {FC, ReactNode, useMemo, useState} from 'react'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query as firebaseQuery,
  where,
} from 'firebase/firestore'
import {useQuery} from 'react-query'
import {copyToClipboard} from '../../../../../../jsFunctions'
import {
  PrintFlowPanel,
} from '../../../print-story'
import {Content} from '../core/_models'

type PrintHistoryDoc = {
  id: string
  [key: string]: any
}

export type ContentPrintHistoryMode = 'printFlow' | 'json' | 'all'

type Props = {
  content: Content
  mode?: ContentPrintHistoryMode
  enabled?: boolean
  onOpenJson?: (focus?: string) => void
}

const EMPTY_TEXT = 'not available'
const PRINT_HISTORY_REFETCH_INTERVAL = 5_000

const getLivePrintHistoryQueryOptions = (enabled: boolean) => ({
  cacheTime: 0,
  enabled,
  refetchInterval: enabled ? PRINT_HISTORY_REFETCH_INTERVAL : false,
  refetchIntervalInBackground: true,
  refetchOnReconnect: true,
  refetchOnWindowFocus: true,
  staleTime: 0,
} as const)

type JourneyTone = 'success' | 'warning' | 'danger' | 'neutral'
type JourneyVerdictKey =
  | 'not_requested'
  | 'ready'
  | 'published_waiting_callback'
  | 'completed'
  | 'failed'
  | 'attention'
  | 'printer_offline'
  | 'printer_stale'
  | 'render_failed'
  | 'prefetch_pending'
  | 'prefetch_callback_missing'
type JourneyStepState = 'done' | 'waiting' | 'failed' | 'skipped' | 'not_requested' | 'unknown'
type CopyMode = 'single' | 'split-copy' | 'native-copies' | 'unknown'
type PrinterFreshnessState = 'fresh' | 'stale' | 'expired' | 'missing'
type OperationalPrinterHealth = 'online' | 'stale' | 'offline' | 'unknown'

type PrinterFreshness = {
  state: PrinterFreshnessState
  lastSeenAtMs?: number
  ageMs?: number
  expiresAtMs?: number
  isExpired: boolean
  label: string
}

type PrintJourneyState = {
  verdict: {
    key: JourneyVerdictKey
    label: string
    tone: JourneyTone
    message: string
    lastActivityAt: any
  }
  summary: {
    printRequested: boolean
    requestSource: string
    codeUsed: string | null
    codeType: string | null
    assignedPrinter: string | null
    copiesRequested: number
    copiesPublished: number
    copiesConfirmed: number
    renderStatus: string
    prefetchStatus: string
    printStatus: string
    callbackStatus: string
    printerHealth: string
  }
  timeline: Array<{
    key: string
    label: string
    state: JourneyStepState
    detail: string
    timestamp?: any
  }>
  activity: {
    prefetchDevices: string[]
    prefetchTotal: number
    prefetchConfirmed: number
    prefetchPending: number
    prefetchFailed: number
    printTotal: number
    printPublished: number
    printCompleted: number
    printFailed: number
    printWaitingCallback: number
    copyMode: CopyMode
  }
}

const toMillis = (value: any): number | undefined => {
  if (!value) {
    return undefined
  }

  if (typeof value === 'number') {
    return value < 1000000000000 ? value * 1000 : value
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  if (value instanceof Date) {
    return value.getTime()
  }

  if (typeof value.toMillis === 'function') {
    return value.toMillis()
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().getTime()
  }

  const seconds = value.seconds ?? value._seconds
  return typeof seconds === 'number' ? seconds * 1000 : undefined
}

const formatTimestamp = (value: any): string => {
  const millis = toMillis(value)

  if (!millis) {
    return EMPTY_TEXT
  }

  return new Date(millis).toLocaleString(undefined, {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatAge = (ageMs?: number): string => {
  if (ageMs === undefined || ageMs < 0) {
    return EMPTY_TEXT
  }

  const seconds = Math.floor(ageMs / 1000)

  if (seconds < 60) {
    return `${seconds}s ago`
  }

  const minutes = Math.floor(seconds / 60)

  if (minutes < 60) {
    return `${minutes}m ago`
  }

  const hours = Math.floor(minutes / 60)

  if (hours < 24) {
    return `${hours}h ago`
  }

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const getPrinterHeartbeatMillis = (printerStatus?: PrintHistoryDoc): number | undefined => {
  return getLatestMillis([
    printerStatus?.lastPingTimestamp,
    printerStatus?.lastPing,
    printerStatus?.updatedAt,
  ])
}

const getPrinterExpiresAtMillis = (printerStatus?: PrintHistoryDoc): number | undefined => {
  return toMillis(printerStatus?.expiresAt)
}

const getPrinterFreshness = (printerStatus?: PrintHistoryDoc): PrinterFreshness => {
  if (!printerStatus || printerStatus.missing) {
    return {
      state: 'missing',
      isExpired: false,
      label: 'Missing heartbeat',
    }
  }

  const now = Date.now()
  const lastSeenAtMs = getPrinterHeartbeatMillis(printerStatus)
  const ageMs = lastSeenAtMs ? Math.max(0, now - lastSeenAtMs) : undefined
  const expiresAtMs = getPrinterExpiresAtMillis(printerStatus)
  const isExpired = Boolean(expiresAtMs && expiresAtMs < now)

  if (isExpired) {
    return {
      state: 'expired',
      lastSeenAtMs,
      ageMs,
      expiresAtMs,
      isExpired,
      label: lastSeenAtMs
        ? `Expired. Last ping: ${formatTimestamp(lastSeenAtMs)}`
        : 'Expired heartbeat',
    }
  }

  if (lastSeenAtMs && ageMs !== undefined && ageMs <= 60000) {
    return {
      state: 'fresh',
      lastSeenAtMs,
      ageMs,
      expiresAtMs,
      isExpired,
      label: `Fresh. Last ping: ${formatTimestamp(lastSeenAtMs)}`,
    }
  }

  if (lastSeenAtMs) {
    return {
      state: 'stale',
      lastSeenAtMs,
      ageMs,
      expiresAtMs,
      isExpired,
      label: `Stale heartbeat. Last ping: ${formatTimestamp(lastSeenAtMs)}`,
    }
  }

  return {
    state: 'missing',
    expiresAtMs,
    isExpired,
    label: 'Missing heartbeat',
  }
}

const getOperationalPrinterHealth = (printerStatus?: PrintHistoryDoc): OperationalPrinterHealth => {
  if (!printerStatus || printerStatus.missing) {
    return 'unknown'
  }

  const freshness = getPrinterFreshness(printerStatus)

  if (printerStatus.isOnline === false) {
    return 'offline'
  }

  if (freshness.state === 'expired') {
    return 'offline'
  }

  if (freshness.state === 'stale') {
    return 'stale'
  }

  if (printerStatus.isOnline === true && freshness.state === 'fresh') {
    return 'online'
  }

  return 'unknown'
}

const getValue = (record: any, path: string): any => {
  return path.split('.').reduce((acc, key) => acc?.[key], record)
}

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return EMPTY_TEXT
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (Array.isArray(value)) {
    return String(value.length)
  }

  if (typeof value === 'object') {
    const formattedDate = formatTimestamp(value)
    return formattedDate !== EMPTY_TEXT ? formattedDate : JSON.stringify(value)
  }

  return String(value)
}

const isMissingValue = (value: any): boolean => {
  return value === null || value === undefined || value === ''
}

const getStatusBadgeClass = (status: any): string => {
  const normalizedStatus = String(status || '').toLowerCase()

  if (['completed', 'complete', 'done', 'success', 'succeeded'].includes(normalizedStatus)) {
    return 'badge-light-success'
  }

  if (
    ['failed', 'error', 'errored', 'cancelled', 'canceled', 'offline', 'expired'].includes(
      normalizedStatus
    )
  ) {
    return 'badge-light-danger'
  }

  if (normalizedStatus === 'published') {
    return 'badge-light-primary'
  }

  if (
    [
      'pending',
      'waiting',
      'queued',
      'stale',
      'waiting_for_composite',
      'sent_no_callback:pending',
    ].includes(normalizedStatus)
  ) {
    return 'badge-light-warning'
  }

  return 'badge-light-secondary'
}

const formatJson = (value: any): string => {
  return JSON.stringify(
    value,
    (_key, item) => {
      if (item && typeof item.toDate === 'function') {
        return item.toDate().toISOString()
      }

      if (item && typeof item.toMillis === 'function') {
        return new Date(item.toMillis()).toISOString()
      }

      return item
    },
    2
  )
}

const getDocId = (record: PrintHistoryDoc, fallbackField: string): string => {
  return record[fallbackField] || record.id
}

const getJobUrl = (job: PrintHistoryDoc): string => {
  return (
    job.signedUrl || job.url || job.storageUrl || job.printableUrl || job.debugOriginalUrl || ''
  )
}

const getCodeUrl = (code: PrintHistoryDoc): string => {
  return code.printableUrl || code.contentUrl || code.url || ''
}

const isPrefetchJob = (job: PrintHistoryDoc): boolean => {
  return job?.type === 'prefetch' || String(job?.jobId || job?.id || '').startsWith('prepare:')
}

const isPrintJob = (job: PrintHistoryDoc): boolean => {
  return (
    job?.type === 'print' ||
    job?.jobKind === 'print' ||
    String(job?.jobId || job?.id || '').startsWith('print:')
  )
}

const getStatusBucket = (status: any): 'completed' | 'failed' | 'published' | 'pending' => {
  const normalizedStatus = String(status || '').toLowerCase()

  if (['completed', 'complete', 'done', 'success', 'succeeded'].includes(normalizedStatus)) {
    return 'completed'
  }

  if (['failed', 'error', 'errored', 'cancelled', 'canceled'].includes(normalizedStatus)) {
    return 'failed'
  }

  if (normalizedStatus === 'published') {
    return 'published'
  }

  return 'pending'
}

const getNumber = (value: any): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

const getFirstNumber = (...values: any[]): number | undefined => {
  for (const value of values) {
    const parsed = getNumber(value)

    if (parsed !== undefined) {
      return parsed
    }
  }

  return undefined
}

const sumNumbers = (records: PrintHistoryDoc[], fields: string[]): number => {
  return records.reduce((total, record) => {
    const value = fields
      .map((field) => getNumber(getValue(record, field)))
      .find((item) => item !== undefined)
    return total + (value || 0)
  }, 0)
}

const isTruthyFlag = (value: any): boolean => {
  return value === true || ['true', 'yes', '1'].includes(String(value || '').toLowerCase())
}

const getCallbackResult = (job: PrintHistoryDoc): string =>
  String(job?.mqttCallback?.result || '').toLowerCase()
const getCallbackStage = (job: PrintHistoryDoc): string =>
  String(job?.mqttCallback?.stage || '').toLowerCase()

const hasFailureCallback = (job: PrintHistoryDoc): boolean => {
  return ['failed', 'failure', 'error'].includes(getCallbackResult(job))
}

const hasPrintSuccessCallback = (job: PrintHistoryDoc): boolean => {
  const stage = getCallbackStage(job)
  return getCallbackResult(job) === 'success' && (!stage || stage === 'print')
}

const hasPrefetchSuccessCallback = (job: PrintHistoryDoc): boolean => {
  return getCallbackResult(job) === 'success' && getCallbackStage(job) === 'prefetch'
}

const isSuccessResult = (value: any): boolean => {
  return ['completed', 'complete', 'done', 'success', 'succeeded'].includes(
    String(value || '').toLowerCase()
  )
}

const isPrintCompleted = (job: PrintHistoryDoc): boolean => {
  return getStatusBucket(job.status) === 'completed' || hasPrintSuccessCallback(job)
}

const isPrefetchCompleted = (job: PrintHistoryDoc): boolean => {
  return getStatusBucket(job.status) === 'completed' || hasPrefetchSuccessCallback(job)
}

const isJobFailed = (job: PrintHistoryDoc): boolean => {
  return getStatusBucket(job.status) === 'failed' || hasFailureCallback(job)
}

const isJobPublished = (job: PrintHistoryDoc): boolean => {
  return (
    getStatusBucket(job.status) === 'published' ||
    isPrintCompleted(job) ||
    isJobFailed(job) ||
    Boolean(
      job.publishedAt ||
        job.timestamps?.publishedAt ||
        job.mqttPayload ||
        job.mqttPublishedAt ||
        job.deviceId
    )
  )
}

const isPrintRequestStatus = (status: any): boolean => {
  const normalizedStatus = String(status || '').toLowerCase()

  if (!normalizedStatus) {
    return false
  }

  if (
    [
      'skipped',
      'not_requested',
      'not requested',
      'not_eligible',
      'not eligible',
      'disabled',
      'ignored',
    ].some((item) => normalizedStatus.includes(item))
  ) {
    return false
  }

  return [
    'requested',
    'queued',
    'pending',
    'published',
    'sent',
    'printing',
    'completed',
    'failed',
  ].some((item) => normalizedStatus.includes(item))
}

const getCodeType = (code?: PrintHistoryDoc): string | null => {
  if (!code) {
    return null
  }

  if (code.isStaffCode === true) {
    return 'Staff'
  }

  if (code.isStaffCode === false) {
    return 'Consumer'
  }

  return code.type ? String(code.type) : null
}

const hasPrintable = (code?: PrintHistoryDoc): boolean => {
  return Boolean(code && (isTruthyFlag(code.printableReady) || getCodeUrl(code)))
}

const getLatestMillis = (values: any[]): number | undefined => {
  return values.reduce<number | undefined>((latest, value) => {
    const millis = toMillis(value)

    if (!millis) {
      return latest
    }

    return !latest || millis > latest ? millis : latest
  }, undefined)
}

const getDocActivityTimes = (record: any): any[] => {
  if (!record) {
    return []
  }

  return [
    record.createdAt,
    record.updatedAt,
    record.completedAt,
    record.failedAt,
    record.publishedAt,
    record.requestedAt,
    record.lastPrintedAt,
    record.lastPing,
    record.lastPingTimestamp,
    record.dateCreated,
    record.updatedOn,
    record.autoPrintRequestedAt,
    record.autoPrintPublishedAt,
    record.autoPrintLastCheckedAt,
    record.createdAtMs,
    record.updatedAtMs,
    record.publishedAtMs,
    record.callbackDueAtMs,
    record.receivedAtMs,
    record.lastEventAtMs,
    record.timestamps?.createdAt,
    record.timestamps?.updatedAt,
    record.timestamps?.publishedAt,
    record.timestamps?.completedAt,
    record.timestamps?.failedAt,
    record.mqttCallback?.timestamp,
    record.mqttCallback?.receivedAt,
    record.mqttCallback?.completedAt,
  ]
}

const compactList = (items: string[], limit = 3): string => {
  if (items.length === 0) {
    return EMPTY_TEXT
  }

  if (items.length <= limit) {
    return items.join(', ')
  }

  return `${items.slice(0, limit).join(', ')} +${items.length - limit} more`
}

const pluralize = (count: number, singular: string, plural = `${singular}s`): string => {
  return `${count} ${count === 1 ? singular : plural}`
}

const humanStatus = (status: string): string => {
  const labels: Record<string, string> = {
    completed: 'Completed',
    expired: 'Expired',
    failed: 'Failed',
    missing: 'Missing',
    online: 'Online',
    offline: 'Offline',
    partial: 'Partial',
    pending: 'Waiting',
    published: 'Published',
    ready: 'Ready',
    stale: 'Stale',
    success: 'Success',
    unknown: 'Unknown',
    waiting: 'Waiting',
  }

  return labels[status] || status.replace(/-/g, ' ')
}

const getCopyMode = (printJobs: PrintHistoryDoc[], requestedCopies: number): CopyMode => {
  if (printJobs.length === 0) {
    return requestedCopies > 0 ? 'unknown' : 'single'
  }

  const copyTotals = printJobs.map((job) => getNumber(job.copyTotal) || 0)
  const perJobCopies = printJobs.map((job) => getFirstNumber(job.copies, job.requestedCopies) || 0)
  const hasSplitCopyFlag = printJobs.some((job) => isTruthyFlag(job.splitCopy))
  const maxCopyTotal = Math.max(0, ...copyTotals)
  const hasNativeCopies = perJobCopies.some((copies) => copies > 1)

  if (
    hasSplitCopyFlag ||
    (printJobs.length > 1 && maxCopyTotal >= printJobs.length && !hasNativeCopies)
  ) {
    return 'split-copy'
  }

  if (hasNativeCopies || printJobs.some((job) => (getNumber(job.requestedCopies) || 0) > 1)) {
    return 'native-copies'
  }

  return 'single'
}

const getRequestedCopies = (content: any, printJobs: PrintHistoryDoc[]): number => {
  const contentCopies = getFirstNumber(
    content?.autoPrintExpectedCopies,
    content?.autoPrintRequestedCopies,
    content?.autoPrintCopies,
    content?.lastPrintExpectedCopies,
    content?.lastPrintRequestedCopies
  )

  if (contentCopies && contentCopies > 0) {
    return contentCopies
  }

  const maxCopyTotal = Math.max(0, ...printJobs.map((job) => getNumber(job.copyTotal) || 0))

  if (maxCopyTotal > 0) {
    return maxCopyTotal
  }

  const requestedCopies = sumNumbers(printJobs, ['requestedCopies'])

  if (requestedCopies > 0) {
    return requestedCopies
  }

  const copies = sumNumbers(printJobs, ['copies'])
  return copies > 0 ? copies : 0
}

const getCompletedJobCopies = (job: PrintHistoryDoc): number => {
  return (
    getFirstNumber(
      job.mqttCallback?.confirmedCopies,
      job.mqttCallback?.printedCopies,
      job.mqttCallback?.copies,
      job.mqttCallback?.copyCount,
      job.copies,
      job.requestedCopies
    ) || 1
  )
}

const getContentConfirmedCopies = (content: any): number | undefined => {
  return getFirstNumber(
    content?.autoPrintConfirmedCopies,
    content?.autoPrintCopiesPrinted,
    content?.autoPrintLastCopiesPrinted,
    content?.lastPrintConfirmedCopies,
    content?.lastPrintCopiesPrinted,
    content?.usedPrints
  )
}

const getContentPublishedCopies = (content: any): number | undefined => {
  return getFirstNumber(content?.autoPrintPublishedCopies)
}

const getContentCopyConfirmationValue = (content: any): string => {
  return String(
    content?.autoPrintCopyConfirmationStatus ||
      content?.lastPrintCopyConfirmationStatus ||
      content?.autoPrintPrintCompletion ||
      content?.lastPrintCompletion ||
      content?.autoPrintStatus ||
      ''
  ).toLowerCase()
}

const hasContentPartialCopyConfirmation = (
  content: any,
  requested: number,
  confirmed: number
): boolean => {
  const status = getContentCopyConfirmationValue(content)

  return (
    status === 'partial' ||
    status.includes('missing_count') ||
    status.includes('partial') ||
    status.includes('assumed') ||
    (requested > 0 && confirmed > 0 && confirmed < requested)
  )
}

const hasContentCompleteCopyConfirmation = (
  content: any,
  requested: number,
  confirmed: number
): boolean => {
  const status = getContentCopyConfirmationValue(content)

  if (
    status.includes('missing_count') ||
    status.includes('partial') ||
    status.includes('assumed')
  ) {
    return false
  }

  return (
    ['completed', 'complete', 'confirmed', 'success', 'succeeded'].includes(status) ||
    status.includes('all_copies') ||
    status.includes('matched') ||
    (requested > 0 && confirmed >= requested)
  )
}

const getPublishedCopies = (printJobs: PrintHistoryDoc[], copyMode: CopyMode): number => {
  const publishedJobs = printJobs.filter(isJobPublished)

  if (copyMode === 'split-copy') {
    return publishedJobs.length
  }

  if (copyMode === 'native-copies') {
    const copies = sumNumbers(publishedJobs, ['copies', 'requestedCopies'])
    return copies > 0 ? copies : publishedJobs.length
  }

  return publishedJobs.length || sumNumbers(printJobs, ['copies'])
}

const getConfirmedCopies = (printJobs: PrintHistoryDoc[], copyMode: CopyMode): number => {
  const completedJobs = printJobs.filter(isPrintCompleted)

  if (copyMode === 'split-copy') {
    return completedJobs.length
  }

  if (copyMode === 'native-copies') {
    return completedJobs.reduce((total, job) => total + getCompletedJobCopies(job), 0)
  }

  return completedJobs.length
}

const getRequestSource = (content: any, printJobs: PrintHistoryDoc[]): string => {
  const rawSource = String(
    content?.autoPrintSource || printJobs.find((job) => job.source)?.source || ''
  ).toLowerCase()

  if (content?.autoPrint === true || rawSource.includes('auto')) {
    return 'Auto-print'
  }

  if (rawSource.includes('staff')) {
    return 'Staff print'
  }

  if (rawSource.includes('qr')) {
    return 'QR'
  }

  if (rawSource.includes('manual')) {
    return 'Manual'
  }

  if (rawSource) {
    return rawSource.replace(/(^|[-_\s])\w/g, (part) => part.toUpperCase()).replace(/[-_]/g, ' ')
  }

  return printJobs.length > 0 ? 'Manual' : 'None'
}

const findJourneyCode = (content: any, codes: PrintHistoryDoc[]): PrintHistoryDoc | undefined => {
  const preferredCodeId =
    content?.autoPrintCodeId ||
    content?.staffCodeId ||
    codes.find((code) => code.isStaffCode === true)?.codeId ||
    codes.find((code) => code.isStaffCode === true)?.id ||
    codes[0]?.codeId ||
    codes[0]?.id

  return (
    codes.find((code) => getDocId(code, 'codeId') === preferredCodeId) ||
    codes.find((code) => code.isStaffCode === true) ||
    codes[0]
  )
}

const getErrorText = (records: PrintHistoryDoc[]): string => {
  const error = records.find(
    (record) =>
      record.lastError || record.error || record.errorDetail || record.mqttCallback?.message
  )
  return error
    ? String(error.lastError || error.error || error.errorDetail || error.mqttCallback?.message)
    : ''
}

const getStepStateFromStatus = (status: string): JourneyStepState => {
  if (status === 'completed' || status === 'published') {
    return 'done'
  }

  if (status === 'failed') {
    return 'failed'
  }

  if (status === 'partial' || status === 'pending' || status === 'waiting') {
    return 'waiting'
  }

  return 'unknown'
}

const getEvidenceSortMillis = (record?: PrintHistoryDoc): number => {
  return getLatestMillis(getDocActivityTimes(record)) || 0
}

const getLatestRecord = (
  records: PrintHistoryDoc[],
  predicate: (record: PrintHistoryDoc) => boolean = () => true
): PrintHistoryDoc | undefined => {
  return records
    .filter(predicate)
    .sort((first, second) => getEvidenceSortMillis(second) - getEvidenceSortMillis(first))[0]
}

const getRecordDeviceId = (record?: PrintHistoryDoc): string => String(record?.deviceId || '')

const getRecordJobKind = (record?: PrintHistoryDoc): string => {
  return String(record?.jobKind || record?.type || '').toLowerCase()
}

const isObserverClientId = (clientId?: any): boolean => {
  return String(clientId || '').startsWith('presence-observer')
}

const hasTopicSubscription = (topics: any, topicSuffix: string): boolean | undefined => {
  if (!Array.isArray(topics)) {
    return undefined
  }

  return topics.some((topic) => String(topic || '').includes(topicSuffix))
}

const getCommandPresenceEvidence = (commandLog?: PrintHistoryDoc): any =>
  commandLog?.presenceEvidence || {}

const getPresenceClientId = (
  connection?: PrintHistoryDoc,
  commandLog?: PrintHistoryDoc
): string => {
  const presenceEvidence = getCommandPresenceEvidence(commandLog)
  return String(presenceEvidence.emqxClientId || connection?.clientId || '')
}

const getPresenceLastEventAt = (
  connection?: PrintHistoryDoc,
  commandLog?: PrintHistoryDoc
): any => {
  const presenceEvidence = getCommandPresenceEvidence(commandLog)
  return presenceEvidence.emqxLastEventAtMs || connection?.lastEventAtMs || connection?.updatedAt
}

const getPresenceConnected = (
  connection?: PrintHistoryDoc,
  commandLog?: PrintHistoryDoc
): boolean | undefined => {
  const presenceEvidence = getCommandPresenceEvidence(commandLog)

  if (typeof presenceEvidence.emqxConnected === 'boolean') {
    return presenceEvidence.emqxConnected
  }

  return typeof connection?.connected === 'boolean' ? connection.connected : undefined
}

const getPresenceStale = (
  connection?: PrintHistoryDoc,
  commandLog?: PrintHistoryDoc
): boolean | undefined => {
  const presenceEvidence = getCommandPresenceEvidence(commandLog)

  if (typeof presenceEvidence.emqxPresenceStale === 'boolean') {
    return presenceEvidence.emqxPresenceStale
  }

  const lastEventAtMs = toMillis(getPresenceLastEventAt(connection, commandLog))
  return lastEventAtMs ? Date.now() - lastEventAtMs > 60000 : undefined
}

const getPresenceSubscribed = (
  connection: PrintHistoryDoc | undefined,
  commandLog: PrintHistoryDoc | undefined,
  kind: 'prefetch' | 'print'
): boolean | undefined => {
  const presenceEvidence = getCommandPresenceEvidence(commandLog)
  const presenceFlag =
    kind === 'prefetch' ? presenceEvidence.subscribedToPrefetch : presenceEvidence.subscribedToPrint

  if (typeof presenceFlag === 'boolean') {
    return presenceFlag
  }

  const topics = presenceEvidence.subscribedTopics || connection?.subscribedTopics
  return hasTopicSubscription(topics, `/${kind}`)
}

const isUnresolvedMishap = (mishap: PrintHistoryDoc): boolean => {
  return mishap.resolved !== true && String(mishap.resolved || '').toLowerCase() !== 'true'
}

const getLatestPrefetchCallbackMissingMishap = (
  mishaps: PrintHistoryDoc[]
): PrintHistoryDoc | undefined => {
  return getLatestRecord(
    mishaps,
    (mishap) =>
      String(mishap.type || '').toLowerCase() === 'prefetch_callback_missing' &&
      isUnresolvedMishap(mishap)
  )
}

const getRelevantRecords = (
  records: PrintHistoryDoc[],
  assignedPrinter?: string | null
): PrintHistoryDoc[] => {
  if (!assignedPrinter) {
    return records
  }

  const matchingRecords = records.filter((record) => getRecordDeviceId(record) === assignedPrinter)
  return matchingRecords.length > 0 ? matchingRecords : records
}

const getLatestRelevantCommandLog = (
  commandLogs: PrintHistoryDoc[],
  assignedPrinter?: string | null,
  preferredKind?: 'prefetch' | 'print'
): PrintHistoryDoc | undefined => {
  const relevantLogs = getRelevantRecords(commandLogs, assignedPrinter)

  if (preferredKind) {
    const preferredLog = getLatestRecord(
      relevantLogs,
      (log) => getRecordJobKind(log) === preferredKind
    )

    if (preferredLog) {
      return preferredLog
    }
  }

  return (
    getLatestRecord(relevantLogs, (log) => getRecordJobKind(log) === 'prefetch') ||
    getLatestRecord(relevantLogs, (log) => getRecordJobKind(log) === 'print') ||
    getLatestRecord(relevantLogs)
  )
}

const getLatestRelevantCallbackLog = (
  callbackLogs: PrintHistoryDoc[],
  assignedPrinter?: string | null,
  jobId?: string
): PrintHistoryDoc | undefined => {
  if (jobId) {
    const matchingJobCallback = getLatestRecord(
      callbackLogs,
      (callback) => callback.jobId === jobId
    )

    if (matchingJobCallback) {
      return matchingJobCallback
    }
  }

  return getLatestRecord(getRelevantRecords(callbackLogs, assignedPrinter))
}

const getPrefetchEvidenceDetail = ({
  assignedPrinter,
  commandLogs,
  connection,
  mishaps,
  prefetchDevices,
}: {
  assignedPrinter: string | null
  commandLogs: PrintHistoryDoc[]
  connection?: PrintHistoryDoc
  mishaps: PrintHistoryDoc[]
  prefetchDevices: string[]
}): string => {
  const latestPrefetchCommand = getLatestRelevantCommandLog(
    commandLogs,
    assignedPrinter,
    'prefetch'
  )
  const commandDevices = Array.from(
    new Set(
      commandLogs
        .filter((log) => getRecordJobKind(log) === 'prefetch')
        .map((log) => getRecordDeviceId(log))
        .filter(Boolean)
    )
  )
  const devices = prefetchDevices.length > 0 ? prefetchDevices : commandDevices
  const detailParts: string[] = []

  if (latestPrefetchCommand?.mqttPublishOk === true || latestPrefetchCommand) {
    detailParts.push(`Backend published prefetch to ${compactList(devices)}.`)
  } else {
    detailParts.push(`Backend prefetch publish evidence is ${EMPTY_TEXT}.`)
  }

  if (getLatestPrefetchCallbackMissingMishap(mishaps)) {
    detailParts.push('No callback received before timeout. Mishap recorded.')
  } else {
    detailParts.push('No callback confirmed yet.')
  }

  if (getPresenceSubscribed(connection, latestPrefetchCommand, 'prefetch') === true) {
    detailParts.push(
      `EMQX subscription evidence found for ${assignedPrinter || compactList(devices)} /prefetch.`
    )
  }

  if (isObserverClientId(getPresenceClientId(connection, latestPrefetchCommand))) {
    detailParts.push('Latest EMQX evidence is from observer/test client.')
  }

  return detailParts.join(' ')
}

const getContentPrintJourneyState = ({
  callbackLogs,
  codes,
  commandLogs,
  content,
  mishaps,
  prefetchJobs,
  printerConnections,
  printerStatuses,
  printJobs,
  renderJobs,
}: {
  content: any
  codes: PrintHistoryDoc[]
  renderJobs: PrintHistoryDoc[]
  prefetchJobs: PrintHistoryDoc[]
  printJobs: PrintHistoryDoc[]
  printerStatuses: PrintHistoryDoc[]
  printerConnections: PrintHistoryDoc[]
  commandLogs: PrintHistoryDoc[]
  callbackLogs: PrintHistoryDoc[]
  mishaps: PrintHistoryDoc[]
}): PrintJourneyState => {
  const selectedCode = findJourneyCode(content, codes)
  const codeUsed = selectedCode
    ? getDocId(selectedCode, 'codeId')
    : content?.autoPrintCodeId || content?.staffCodeId || null
  const codeType = getCodeType(selectedCode)
  const assignedPrinter =
    printJobs.find((job) => job.deviceId)?.deviceId ||
    selectedCode?.deviceId ||
    codes.find((code) => code.deviceId)?.deviceId ||
    null
  const printerStatus = assignedPrinter
    ? printerStatuses.find(
        (status) => status.deviceId === assignedPrinter || status.id === assignedPrinter
      )
    : undefined
  const printerConnection = assignedPrinter
    ? printerConnections.find(
        (connection) => connection.deviceId === assignedPrinter || connection.id === assignedPrinter
      )
    : undefined
  const printerFreshness = getPrinterFreshness(printerStatus)
  const printerHealth = getOperationalPrinterHealth(printerStatus)
  const printableReady = hasPrintable(selectedCode) || codes.some(hasPrintable)
  const detectedRequestSource = getRequestSource(content, printJobs)
  const requestedCopies = getRequestedCopies(content, printJobs)
  const printRequested = Boolean(
    content?.autoPrint === true ||
      content?.autoPrintRequestId ||
      isPrintRequestStatus(content?.autoPrintStatus) ||
      content?.autoPrintRequestedAt ||
      content?.autoPrintCodeId ||
      requestedCopies > 0 ||
      printJobs.length > 0
  )
  const requestSource = printRequested ? detectedRequestSource : 'None'
  const copyMode = getCopyMode(printJobs, requestedCopies)
  const copiesPublished =
    getContentPublishedCopies(content) ?? getPublishedCopies(printJobs, copyMode)
  const copiesConfirmed =
    getContentConfirmedCopies(content) ?? getConfirmedCopies(printJobs, copyMode)
  const contentCopyPartial = hasContentPartialCopyConfirmation(
    content,
    requestedCopies,
    copiesConfirmed
  )
  const contentCopyComplete = hasContentCompleteCopyConfirmation(
    content,
    requestedCopies,
    copiesConfirmed
  )
  const renderFailed = renderJobs.some(isJobFailed)
  const renderCompleted =
    renderJobs.some((job) => getStatusBucket(job.status) === 'completed') || printableReady
  const renderStatus = renderFailed
    ? 'failed'
    : renderCompleted
    ? 'completed'
    : renderJobs.length > 0
    ? 'waiting'
    : 'missing'
  const relevantPrefetchJobs = assignedPrinter
    ? prefetchJobs.filter((job) => job.deviceId === assignedPrinter)
    : prefetchJobs
  const prefetchJobsForStatus =
    relevantPrefetchJobs.length > 0 ? relevantPrefetchJobs : prefetchJobs
  const prefetchFailed = prefetchJobsForStatus.some(isJobFailed)
  const prefetchCompleted = prefetchJobsForStatus.some(isPrefetchCompleted)
  const prefetchStatus = prefetchFailed
    ? 'failed'
    : prefetchCompleted
    ? 'completed'
    : prefetchJobs.length > 0
    ? 'pending'
    : 'missing'
  const printFailed = printJobs.some(isJobFailed)
  const printCompleted = printJobs.some(isPrintCompleted)
  const printPublished = printJobs.some(isJobPublished)
  const printStatus = printFailed
    ? 'failed'
    : contentCopyComplete || (printCompleted && !contentCopyPartial)
    ? 'completed'
    : contentCopyPartial
    ? 'partial'
    : printPublished
    ? 'published'
    : 'missing'
  const callbackStatus = printFailed
    ? 'failed'
    : contentCopyComplete || (printCompleted && !contentCopyPartial)
    ? 'completed'
    : contentCopyPartial
    ? 'partial'
    : printPublished
    ? 'waiting'
    : 'missing'
  const prefetchDevices = Array.from(
    new Set(
      prefetchJobs
        .map((job) => job.deviceId)
        .filter((deviceId): deviceId is string => Boolean(deviceId))
    )
  )
  const prefetchCallbackMissingMishap = getLatestPrefetchCallbackMissingMishap(mishaps)
  const prefetchCallbackCompleted = callbackLogs.some(
    (callback) =>
      String(callback.stage || '').toLowerCase() === 'prefetch' &&
      ['success', 'completed', 'complete'].includes(String(callback.result || '').toLowerCase())
  )
  const prefetchCallbackMissingActive = Boolean(
    prefetchCallbackMissingMishap && prefetchStatus !== 'completed' && !prefetchCallbackCompleted
  )
  const prefetchConfirmed = prefetchJobs.filter(isPrefetchCompleted).length
  const prefetchFailedCount = prefetchJobs.filter(isJobFailed).length
  const printCompletedCount = printJobs.filter(isPrintCompleted).length
  const printFailedCount = printJobs.filter(isJobFailed).length
  const printPublishedCount = printJobs.filter(isJobPublished).length
  const printWaitingCallback = Math.max(
    0,
    printPublishedCount - printCompletedCount - printFailedCount
  )
  const renderError = getErrorText(renderJobs)
  const printError = getErrorText(printJobs)
  const copyText = requestedCopies > 0 ? ` for ${pluralize(requestedCopies, 'copy', 'copies')}` : ''
  const sourceText = requestSource === 'None' ? 'Print' : requestSource
  const printerText = assignedPrinter || 'the assigned printer'
  const codeText = codeUsed ? ` using code ${codeUsed}` : ''
  const hasPhysicalOrPrefetchWork =
    printRequested || prefetchJobs.length > 0 || printJobs.length > 0
  const printerLastPingText = printerFreshness.lastSeenAtMs
    ? ` Last ping: ${formatTimestamp(printerFreshness.lastSeenAtMs)}.`
    : ''
  const prefetchEvidenceDetail = getPrefetchEvidenceDetail({
    assignedPrinter,
    commandLogs,
    connection: printerConnection,
    mishaps,
    prefetchDevices,
  })
  const latestActivityAt = getLatestMillis([
    ...getDocActivityTimes(content),
    ...codes.flatMap(getDocActivityTimes),
    ...renderJobs.flatMap(getDocActivityTimes),
    ...prefetchJobs.flatMap(getDocActivityTimes),
    ...printJobs.flatMap(getDocActivityTimes),
    ...printerStatuses.flatMap(getDocActivityTimes),
    ...printerConnections.flatMap(getDocActivityTimes),
    ...commandLogs.flatMap(getDocActivityTimes),
    ...callbackLogs.flatMap(getDocActivityTimes),
    ...mishaps.flatMap(getDocActivityTimes),
  ])

  let verdict: PrintJourneyState['verdict']

  if (prefetchCallbackMissingActive) {
    verdict = {
      key: 'prefetch_callback_missing',
      label: 'Prefetch callback missing',
      tone: 'warning',
      message:
        'Backend published the prefetch command, but no PrintStation callback was received within 30 seconds. Check EMQX subscription and heartbeat evidence below.',
      lastActivityAt: latestActivityAt,
    }
  } else if (!printRequested && printJobs.length === 0 && prefetchJobs.length > 0) {
    verdict = {
      key: 'not_requested',
      label: 'Printable prefetched, no physical print requested',
      tone: 'neutral',
      message: `Printable assets were prefetched for ${compactList(
        prefetchDevices
      )}, but no physical print job is recorded for this content.`,
      lastActivityAt: latestActivityAt,
    }
  } else if (!printRequested && printJobs.length === 0) {
    verdict = {
      key: 'not_requested',
      label: 'No physical print requested',
      tone: 'neutral',
      message: 'No auto-print or manual physical print request is recorded for this content.',
      lastActivityAt: latestActivityAt,
    }
  } else if (renderStatus === 'failed') {
    verdict = {
      key: 'render_failed',
      label: 'Render failed',
      tone: 'danger',
      message: `${sourceText} was requested${copyText}${codeText}, but the printable could not be generated${
        renderError ? `: ${renderError}` : '.'
      }`,
      lastActivityAt: latestActivityAt,
    }
  } else if (printStatus === 'failed') {
    verdict = {
      key: 'failed',
      label: 'Print failed',
      tone: 'danger',
      message: `Print was published to ${printerText}, but the printer flow reported a failure${
        printError ? `: ${printError}` : '.'
      }`,
      lastActivityAt: latestActivityAt,
    }
  } else if (
    printerHealth === 'offline' &&
    hasPhysicalOrPrefetchWork &&
    printStatus !== 'completed'
  ) {
    verdict = {
      key: 'printer_offline',
      label: 'Printer offline',
      tone: 'danger',
      message: `${sourceText} was requested${copyText}, but ${printerText} is offline.${printerLastPingText}`,
      lastActivityAt: latestActivityAt,
    }
  } else if (
    printerHealth === 'stale' &&
    hasPhysicalOrPrefetchWork &&
    printStatus !== 'completed'
  ) {
    verdict = {
      key: 'printer_stale',
      label: 'Printer heartbeat stale',
      tone: 'warning',
      message: `${printerText} last reported OK, but heartbeat is stale.${printerLastPingText}`,
      lastActivityAt: latestActivityAt,
    }
  } else if (printStatus === 'partial') {
    verdict = {
      key: 'attention',
      label: 'Print partially confirmed',
      tone: 'warning',
      message: `${sourceText} was requested${copyText}. The printer callback confirmed ${pluralize(
        copiesConfirmed,
        'copy',
        'copies'
      )}, but ${pluralize(requestedCopies || copiesPublished, 'copy', 'copies')} ${
        requestedCopies ? 'were expected' : 'were published'
      }.`,
      lastActivityAt: latestActivityAt,
    }
  } else if (printStatus === 'completed') {
    verdict = {
      key: 'completed',
      label: 'Print completed',
      tone: 'success',
      message: `${sourceText} was requested${copyText}. The printable was rendered, the print job was sent to ${printerText}, and the printer confirmed ${pluralize(
        copiesConfirmed || printCompletedCount,
        'copy',
        'copies'
      )}.`,
      lastActivityAt: latestActivityAt,
    }
  } else if (printStatus === 'published') {
    verdict = {
      key: 'published_waiting_callback',
      label: 'Print published - waiting for printer confirmation',
      tone: 'warning',
      message: `${sourceText} was requested${copyText}. The printable was rendered and the print job was sent to ${printerText}, but no printer success callback has been received yet.`,
      lastActivityAt: latestActivityAt,
    }
  } else if (prefetchStatus === 'pending') {
    verdict = {
      key: 'prefetch_pending',
      label: 'Prefetch pending',
      tone: 'warning',
      message: `Printable assets were sent to ${compactList(
        prefetchDevices
      )}, but prefetch confirmation is still pending.`,
      lastActivityAt: latestActivityAt,
    }
  } else if (renderStatus === 'completed' && printStatus === 'missing') {
    verdict = {
      key: 'ready',
      label: 'Ready to print',
      tone: 'neutral',
      message: `The printable is ready${codeText}, but no physical print job has been published yet.`,
      lastActivityAt: latestActivityAt,
    }
  } else {
    verdict = {
      key: 'attention',
      label: 'Needs attention',
      tone: 'warning',
      message:
        'The print request is present, but the journey has not reached a complete print confirmation.',
      lastActivityAt: latestActivityAt,
    }
  }

  const contentTimestamp = getLatestMillis(getDocActivityTimes(content))
  const codeTimestamp = getLatestMillis(getDocActivityTimes(selectedCode))
  const renderTimestamp = getLatestMillis(renderJobs.flatMap(getDocActivityTimes))
  const prefetchTimestamp = getLatestMillis(prefetchJobs.flatMap(getDocActivityTimes))
  const printTimestamp = getLatestMillis(printJobs.flatMap(getDocActivityTimes))
  const printerTimestamp = getLatestMillis(getDocActivityTimes(printerStatus))

  return {
    verdict,
    summary: {
      printRequested,
      requestSource,
      codeUsed,
      codeType,
      assignedPrinter,
      copiesRequested: requestedCopies,
      copiesPublished,
      copiesConfirmed,
      renderStatus,
      prefetchStatus,
      printStatus,
      callbackStatus,
      printerHealth,
    },
    timeline: [
      {
        key: 'content',
        label: 'Content',
        state: content ? 'done' : 'unknown',
        detail: printRequested
          ? 'Print request signals found on this content.'
          : prefetchJobs.length > 0
          ? 'Prefetch activity exists, but no physical print request is recorded.'
          : 'No physical print request found on this content.',
        timestamp: contentTimestamp,
      },
      {
        key: 'code',
        label: 'Code',
        state: selectedCode ? 'done' : printRequested ? 'waiting' : 'not_requested',
        detail: selectedCode
          ? `${codeType || 'Content'} code ${codeUsed || EMPTY_TEXT}`
          : 'No code found for this content yet.',
        timestamp: codeTimestamp,
      },
      {
        key: 'render',
        label: 'Printable Render',
        state: getStepStateFromStatus(renderStatus),
        detail:
          renderStatus === 'completed'
            ? 'Printable created.'
            : renderStatus === 'failed'
            ? renderError || 'Printable generation failed.'
            : renderStatus === 'waiting'
            ? 'Render job is still waiting.'
            : 'No printable render record found.',
        timestamp: renderTimestamp,
      },
      {
        key: 'prefetch',
        label: 'Prefetch',
        state:
          prefetchJobs.length > 0
            ? getStepStateFromStatus(prefetchStatus)
            : !printRequested
            ? 'not_requested'
            : prefetchStatus === 'missing' && printJobs.length > 0
            ? 'skipped'
            : getStepStateFromStatus(prefetchStatus),
        detail:
          prefetchStatus === 'completed'
            ? `Confirmed on ${prefetchConfirmed} of ${prefetchJobs.length} prefetch jobs.`
            : prefetchStatus === 'failed'
            ? getErrorText(prefetchJobs) || 'At least one prefetch job failed.'
            : prefetchStatus === 'pending'
            ? prefetchEvidenceDetail
            : printJobs.length > 0
            ? 'No prefetch record found; print still reached publish.'
            : 'No prefetch job found.',
        timestamp: prefetchTimestamp,
      },
      {
        key: 'print',
        label: 'Print Job',
        state: !printRequested
          ? 'not_requested'
          : printStatus === 'missing'
          ? 'waiting'
          : getStepStateFromStatus(printStatus),
        detail:
          printStatus === 'completed'
            ? `Published to ${printerText}.`
            : printStatus === 'failed'
            ? printError || `Print job failed on ${printerText}.`
            : printStatus === 'partial'
            ? `Published to ${printerText}, but only ${copiesConfirmed} of ${
                requestedCopies || copiesPublished
              } copies are confirmed.`
            : printStatus === 'published'
            ? `Published to ${printerText}.`
            : 'No physical print job has been published yet.',
        timestamp: printTimestamp,
      },
      {
        key: 'callback',
        label: 'Callback / Result',
        state: !printRequested
          ? 'not_requested'
          : callbackStatus === 'missing'
          ? 'skipped'
          : getStepStateFromStatus(callbackStatus),
        detail:
          callbackStatus === 'completed'
            ? `Printer confirmed ${pluralize(
                copiesConfirmed || printCompletedCount,
                'copy',
                'copies'
              )}.`
            : callbackStatus === 'failed'
            ? printError || 'Printer reported a failure.'
            : callbackStatus === 'partial'
            ? `Printer confirmed ${copiesConfirmed} of ${
                requestedCopies || copiesPublished
              } expected copies.`
            : callbackStatus === 'waiting'
            ? 'No print success callback received yet.'
            : 'Print has not reached callback stage.',
        timestamp: printTimestamp,
      },
      {
        key: 'printer',
        label: 'Printer Health',
        state:
          printerHealth === 'online'
            ? 'done'
            : printerHealth === 'offline'
            ? 'failed'
            : printerHealth === 'stale'
            ? 'waiting'
            : assignedPrinter
            ? 'unknown'
            : 'skipped',
        detail: assignedPrinter
          ? printerHealth === 'stale'
            ? `${assignedPrinter} last reported OK, but heartbeat is stale.${printerLastPingText}`
            : printerHealth === 'offline'
            ? `${assignedPrinter} is offline.${printerLastPingText}`
            : printerHealth === 'online'
            ? `${assignedPrinter} is Online.${printerLastPingText}`
            : `${assignedPrinter} is missing a recent heartbeat.${printerLastPingText}`
          : 'No selected printer could be resolved.',
        timestamp: printerTimestamp,
      },
    ],
    activity: {
      prefetchDevices,
      prefetchTotal: prefetchJobs.length,
      prefetchConfirmed,
      prefetchPending: Math.max(0, prefetchJobs.length - prefetchConfirmed - prefetchFailedCount),
      prefetchFailed: prefetchFailedCount,
      printTotal: printJobs.length,
      printPublished: printPublishedCount,
      printCompleted: printCompletedCount,
      printFailed: printFailedCount,
      printWaitingCallback,
      copyMode,
    },
  }
}

const getCollectionDocsByContentId = async (
  collectionName: string,
  contentId: string,
  idField: string
): Promise<PrintHistoryDoc[]> => {
  const db = getFirestore()
  const snapshot = await getDocs(
    firebaseQuery(collection(db, collectionName), where('contentId', '==', contentId))
  )

  return snapshot.docs.map((document) => {
    const data = document.data()

    return {
      id: document.id,
      ...data,
      [idField]: data[idField] || document.id,
    }
  })
}

const getEvidenceDocsByContentId = async (
  collectionName: string,
  contentId: string,
  idField: string
): Promise<PrintHistoryDoc[]> => {
  return getCollectionDocsByContentId(collectionName, contentId, idField)
}

const getRenderJobs = async (contentId: string, codeIds: string[]): Promise<PrintHistoryDoc[]> => {
  const byContentId = await getCollectionDocsByContentId('renderJobs', contentId, 'jobId')

  if (byContentId.length > 0 || codeIds.length === 0) {
    return byContentId
  }

  const db = getFirestore()
  const chunks: string[][] = []

  for (let index = 0; index < codeIds.length; index += 10) {
    chunks.push(codeIds.slice(index, index + 10))
  }

  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      getDocs(firebaseQuery(collection(db, 'renderJobs'), where('codeId', 'in', chunk)))
    )
  )

  const jobs = snapshots
    .flatMap((snapshot) => snapshot.docs)
    .map((document) => {
      const data = document.data()

      return {
        id: document.id,
        ...data,
        jobId: data.jobId || document.id,
      }
    })

  return Array.from(new Map(jobs.map((job) => [job.id, job])).values())
}

const getRawContentDoc = async (contentId: string): Promise<PrintHistoryDoc | null> => {
  const db = getFirestore()
  const snapshot = await getDoc(doc(db, '_contents', contentId))

  if (!snapshot.exists()) {
    return null
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  }
}

const getPrinterStatuses = async (deviceIds: string[]): Promise<PrintHistoryDoc[]> => {
  const db = getFirestore()
  const statuses = await Promise.all(
    deviceIds.map(async (deviceId) => {
      const snapshot = await getDoc(doc(db, 'printerStatus', deviceId))

      if (!snapshot.exists()) {
        return {
          id: deviceId,
          deviceId,
          missing: true,
        }
      }

      return {
        id: snapshot.id,
        deviceId,
        ...snapshot.data(),
      }
    })
  )

  return statuses
}

const getPrinterConnections = async (deviceIds: string[]): Promise<PrintHistoryDoc[]> => {
  const db = getFirestore()
  const uniqueDeviceIds = Array.from(new Set(deviceIds.filter(Boolean)))
  const connections = await Promise.all(
    uniqueDeviceIds.map(async (deviceId) => {
      const snapshot = await getDoc(doc(db, 'printerConnections', deviceId))

      if (!snapshot.exists()) {
        return {
          id: deviceId,
          deviceId,
          missing: true,
        }
      }

      return {
        id: snapshot.id,
        deviceId,
        ...snapshot.data(),
      }
    })
  )

  return connections
}

const SectionState: FC<{
  emptyText: string
  error: unknown
  isLoading: boolean
  isEmpty: boolean
}> = ({emptyText, error, isEmpty, isLoading}) => {
  if (isLoading) {
    return <div className='text-muted fw-semibold py-5'>Loading...</div>
  }

  if (error) {
    return (
      <div className='text-danger fw-semibold py-5'>Unable to load this print history section.</div>
    )
  }

  if (isEmpty) {
    return <div className='text-muted fw-semibold py-5'>{emptyText}</div>
  }

  return null
}

const CopyableValue: FC<{value: any; isTimestamp?: boolean; monospace?: boolean}> = ({
  isTimestamp,
  monospace = true,
  value,
}) => {
  const formattedValue = isTimestamp ? formatTimestamp(value) : formatValue(value)
  const canCopy = !isMissingValue(value) && formattedValue !== EMPTY_TEXT

  return (
    <div className='d-flex align-items-center gap-2 mw-100'>
      <span
        className={`d-inline-block text-truncate ${monospace ? 'font-monospace' : ''}`}
        style={{maxWidth: '100%'}}
        title={formattedValue}
      >
        {formattedValue}
      </span>
      {canCopy && (
        <button
          type='button'
          className='btn btn-icon btn-sm btn-light-primary flex-shrink-0'
          onClick={() => copyToClipboard(formattedValue)}
          title='Copy'
        >
          <i className='bi bi-clipboard'></i>
        </button>
      )}
    </div>
  )
}

const StatusBadge: FC<{status: any}> = ({status}) => (
  <span className={`badge ${getStatusBadgeClass(status)}`}>{formatValue(status)}</span>
)

const SummaryField: FC<{label: string; value: any; isTimestamp?: boolean}> = ({
  isTimestamp,
  label,
  value,
}) => (
  <div className='col-md-6 col-xl-4'>
    <div className='border border-dashed rounded px-4 py-3 h-100'>
      <div className='text-muted fw-semibold fs-8 text-uppercase'>{label}</div>
      <div className='text-gray-800 fw-bold fs-7 mt-1'>
        <CopyableValue value={value} isTimestamp={isTimestamp} />
      </div>
    </div>
  </div>
)

const StatusStripCard: FC<{label: string; value: any; subtext: string; status?: any}> = ({
  label,
  status,
  subtext,
  value,
}) => (
  <div className='col-md-6 col-xl'>
    <div className='border border-dashed rounded px-4 py-4 h-100'>
      <div className='text-muted fw-semibold fs-8 text-uppercase mb-1'>{label}</div>
      <div className='fw-bold text-gray-900 fs-5 mb-1 text-truncate' title={formatValue(value)}>
        {formatValue(value)}
      </div>
      <div className='text-muted fw-semibold fs-7'>{subtext}</div>
      {status !== undefined && (
        <div className='mt-2'>
          <StatusBadge status={status} />
        </div>
      )}
    </div>
  </div>
)

const CountGroup: FC<{title: string; items: Array<[string, number]>}> = ({items, title}) => (
  <div className='col-md-6 col-xl'>
    <div className='border border-dashed rounded p-4 h-100'>
      <div className='fw-bold text-gray-800 mb-3'>{title}</div>
      <div className='d-flex flex-column gap-2'>
        {items.map(([label, value]) => (
          <div className='d-flex justify-content-between align-items-center' key={label}>
            <span className='text-muted fw-semibold'>{label}</span>
            <span className='badge badge-light-primary'>{value}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
)

const CopyButton: FC<{value?: string; label?: string; disabled?: boolean}> = ({
  disabled,
  label = 'Copy',
  value,
}) => (
  <button
    type='button'
    className='btn btn-sm btn-light-primary me-2 mb-2'
    disabled={disabled || !value}
    onClick={() => value && copyToClipboard(value)}
  >
    {label}
  </button>
)

const OpenButton: FC<{url?: string; label?: string}> = ({label = 'Open', url}) => (
  <a
    className={`btn btn-sm btn-light me-2 mb-2 ${url ? '' : 'disabled'}`}
    href={url || '#'}
    target='_blank'
    rel='noopener noreferrer'
    onClick={(event) => {
      if (!url) {
        event.preventDefault()
      }
    }}
  >
    {label}
  </a>
)

const RawDetails: FC<{title: string; value: any}> = ({title, value}) => (
  <details className='mt-2'>
    <summary className='text-primary fw-semibold cursor-pointer'>{title}</summary>
    <pre
      className='bg-light rounded p-3 mt-3 fs-8 text-gray-800 overflow-auto'
      style={{maxHeight: 320}}
    >
      {formatJson(value)}
    </pre>
  </details>
)

const DataCell: FC<{record: PrintHistoryDoc; field: string; isTimestamp?: boolean}> = ({
  field,
  isTimestamp,
  record,
}) => {
  const value = getValue(record, field)

  return (
    <td className='text-gray-800' style={{maxWidth: 220}}>
      <CopyableValue value={value} isTimestamp={isTimestamp} />
    </td>
  )
}

const StatusCell: FC<{record: PrintHistoryDoc; field?: string; value?: any}> = ({
  field = 'status',
  record,
  value,
}) => (
  <td>
    <StatusBadge status={value ?? getValue(record, field)} />
  </td>
)

const SectionCard: FC<{children: ReactNode | ReactNode[]; helper?: string; title: string}> = ({
  children,
  helper,
  title,
}) => (
  <div className='border border-dashed rounded p-5 mb-8'>
    <div className='mb-4'>
      <h4 className='fw-bold text-gray-800 fs-6 mb-1'>{title}</h4>
      {helper && <div className='text-muted fs-7'>{helper}</div>}
    </div>
    {children}
  </div>
)

const getToneClasses = (
  tone: JourneyTone
): {border: string; icon: string; soft: string; text: string} => {
  const classes = {
    danger: {
      border: 'border-danger bg-light-danger',
      icon: 'text-danger',
      soft: 'badge-light-danger',
      text: 'text-danger',
    },
    neutral: {
      border: 'border-primary bg-light-primary',
      icon: 'text-primary',
      soft: 'badge-light-primary',
      text: 'text-primary',
    },
    success: {
      border: 'border-success bg-light-success',
      icon: 'text-success',
      soft: 'badge-light-success',
      text: 'text-success',
    },
    warning: {
      border: 'border-warning bg-light-warning',
      icon: 'text-warning',
      soft: 'badge-light-warning',
      text: 'text-warning',
    },
  }

  return classes[tone]
}

const JourneyChip: FC<{label: string; value: any}> = ({label, value}) => (
  <span className='badge badge-light fw-semibold text-gray-800 py-2 px-3 mw-100'>
    <span className='text-muted me-1'>{label}:</span>
    <span
      className='text-truncate d-inline-block align-bottom'
      style={{maxWidth: 160}}
      title={formatValue(value)}
    >
      {formatValue(value)}
    </span>
  </span>
)

const PrintJourneyVerdictCard: FC<{journey: PrintJourneyState}> = ({journey}) => {
  const tone = getToneClasses(journey.verdict.tone)

  return (
    <div className={`border border-2 rounded p-5 mb-6 ${tone.border}`}>
      <div className='d-flex flex-column flex-lg-row justify-content-between gap-4'>
        <div className='d-flex gap-4'>
          <div className='flex-shrink-0'>
            <i className={`bi bi-printer fs-1 ${tone.icon}`}></i>
          </div>
          <div className='min-w-0'>
            <div className={`fw-bold fs-3 mb-2 ${tone.text}`}>{journey.verdict.label}</div>
            <div className='fw-semibold text-gray-800 fs-6 mb-4' style={{maxWidth: 920}}>
              {journey.verdict.message}
            </div>
            <div className='d-flex flex-wrap gap-2'>
              <JourneyChip
                label='Print requested'
                value={journey.summary.printRequested ? 'Yes' : 'No'}
              />
              <JourneyChip label='Code' value={journey.summary.codeUsed || EMPTY_TEXT} />
              <JourneyChip
                label='Assigned printer'
                value={journey.summary.assignedPrinter || EMPTY_TEXT}
              />
              <JourneyChip label='Copies requested' value={journey.summary.copiesRequested} />
              <JourneyChip label='Confirmed' value={journey.summary.copiesConfirmed} />
              <JourneyChip label='Source' value={journey.summary.requestSource} />
            </div>
          </div>
        </div>
        <div className='text-lg-end flex-shrink-0'>
          <div className='text-muted fw-semibold fs-8 text-uppercase'>Last activity</div>
          <div className='fw-bold text-gray-800'>
            {formatTimestamp(journey.verdict.lastActivityAt)}
          </div>
        </div>
      </div>
    </div>
  )
}

const ObserverSummaryCard: FC<{
  details: string[]
  headline: string
  icon: string
  title: string
  tone?: JourneyTone
}> = ({details, headline, icon, title, tone = 'neutral'}) => {
  const toneClasses = getToneClasses(tone)

  return (
    <div className='col-md-6 col-xl-4'>
      <div className='border border-dashed rounded px-4 py-4 h-100 overflow-hidden'>
        <div className='d-flex align-items-center gap-3 mb-3'>
          <i className={`bi ${icon} fs-3 ${toneClasses.icon}`}></i>
          <div className='text-muted fw-semibold fs-8 text-uppercase'>{title}</div>
        </div>
        <div className='fw-bold text-gray-900 fs-5 mb-2 text-truncate' title={headline}>
          {headline}
        </div>
        <div className='d-flex flex-column gap-1'>
          {details.map((detail) => (
            <div className='text-muted fw-semibold fs-7 text-truncate' key={detail} title={detail}>
              {detail}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const getStatusTone = (status: string): JourneyTone => {
  if (['expired', 'failed', 'offline'].includes(status)) {
    return 'danger'
  }

  if (['pending', 'stale', 'waiting', 'published'].includes(status)) {
    return 'warning'
  }

  if (['completed', 'online', 'success', 'succeeded'].includes(status)) {
    return 'success'
  }

  return 'neutral'
}

const SmartSummaryRow: FC<{journey: PrintJourneyState; printerStatus?: PrintHistoryDoc}> = ({
  journey,
  printerStatus,
}) => {
  const renderStep = journey.timeline.find((step) => step.key === 'render')
  const printerFreshness = getPrinterFreshness(printerStatus)
  const printerLastPing = printerFreshness.lastSeenAtMs
    ? formatTimestamp(printerFreshness.lastSeenAtMs)
    : EMPTY_TEXT

  return (
    <div className='row g-4 mb-8'>
      <ObserverSummaryCard
        title='Request'
        icon='bi-lightning-charge'
        headline={journey.summary.printRequested ? 'Requested' : 'Not requested'}
        tone={journey.summary.printRequested ? 'success' : 'neutral'}
        details={[
          `Source: ${journey.summary.requestSource}`,
          `Copies requested: ${journey.summary.copiesRequested}`,
        ]}
      />
      <ObserverSummaryCard
        title='Code'
        icon='bi-upc-scan'
        headline={journey.summary.codeUsed || 'No code found'}
        tone={journey.summary.codeUsed ? 'success' : 'warning'}
        details={[
          journey.summary.codeType || 'Type unknown',
          `Printable: ${journey.summary.renderStatus === 'completed' ? 'Ready' : 'Not ready'}`,
        ]}
      />
      <ObserverSummaryCard
        title='Render'
        icon='bi-file-earmark-image'
        headline={humanStatus(journey.summary.renderStatus)}
        tone={getStatusTone(journey.summary.renderStatus)}
        details={[
          journey.summary.renderStatus === 'failed' && renderStep?.detail
            ? renderStep.detail
            : journey.summary.renderStatus === 'completed'
            ? 'Printable ready'
            : 'No completed printable yet',
        ]}
      />
      <ObserverSummaryCard
        title='Prefetch'
        icon='bi-cloud-arrow-down'
        headline={humanStatus(journey.summary.prefetchStatus)}
        tone={getStatusTone(journey.summary.prefetchStatus)}
        details={[
          `Devices targeted: ${journey.activity.prefetchDevices.length}`,
          `Confirmed: ${journey.activity.prefetchConfirmed}`,
        ]}
      />
      <ObserverSummaryCard
        title='Print'
        icon='bi-printer'
        headline={humanStatus(journey.summary.printStatus)}
        tone={getStatusTone(journey.summary.printStatus)}
        details={[
          `Selected printer: ${journey.summary.assignedPrinter || EMPTY_TEXT}`,
          `Confirmed prints: ${journey.summary.copiesConfirmed}`,
        ]}
      />
      <ObserverSummaryCard
        title='Printer'
        icon='bi-broadcast'
        headline={humanStatus(journey.summary.printerHealth)}
        tone={getStatusTone(journey.summary.printerHealth)}
        details={[
          `Last ping: ${printerLastPing}`,
          printerStatus?.status !== undefined
            ? `Last reported: ${formatValue(printerStatus.status)}`
            : 'Last reported unknown',
          printerStatus?.paper !== undefined
            ? `Paper: ${formatValue(printerStatus.paper)}`
            : 'Paper unknown',
          printerStatus?.ink !== undefined
            ? `Ink: ${formatValue(printerStatus.ink)}`
            : 'Ink unknown',
        ]}
      />
    </div>
  )
}

const formatYesNoUnknown = (value: boolean | undefined): string => {
  if (value === undefined) {
    return 'Unknown'
  }

  return value ? 'Yes' : 'No'
}

const EvidenceField: FC<{label: string; value: any; isTimestamp?: boolean; status?: any}> = ({
  isTimestamp,
  label,
  status,
  value,
}) => (
  <div className='d-flex justify-content-between align-items-start gap-3 py-1'>
    <span className='text-muted fw-semibold fs-7'>{label}</span>
    <span className='fw-bold text-gray-800 text-end text-break' style={{maxWidth: 240}}>
      {status !== undefined ? (
        <StatusBadge status={status} />
      ) : isTimestamp ? (
        formatTimestamp(value)
      ) : (
        formatValue(value)
      )}
    </span>
  </div>
)

const EvidenceCard: FC<{
  children: ReactNode | ReactNode[]
  icon: string
  title: string
  tone?: JourneyTone
}> = ({children, icon, title, tone = 'neutral'}) => {
  const toneClasses = getToneClasses(tone)

  return (
    <div className='col-md-6 col-xl'>
      <div className='border border-dashed rounded p-4 h-100'>
        <div className='d-flex align-items-center gap-3 mb-3'>
          <i className={`bi ${icon} fs-3 ${toneClasses.icon}`}></i>
          <div className='fw-bold text-gray-800'>{title}</div>
        </div>
        <div className='d-flex flex-column gap-1'>{children}</div>
      </div>
    </div>
  )
}

const DeliveryEvidenceSection: FC<{
  callbackLogs: PrintHistoryDoc[]
  commandLogs: PrintHistoryDoc[]
  error: unknown
  isLoading: boolean
  jobs: PrintHistoryDoc[]
  journey: PrintJourneyState
  mishaps: PrintHistoryDoc[]
  printerConnection?: PrintHistoryDoc
  printerStatus?: PrintHistoryDoc
}> = ({
  callbackLogs,
  commandLogs,
  error,
  isLoading,
  jobs,
  journey,
  mishaps,
  printerConnection,
  printerStatus,
}) => {
  const assignedPrinter = journey.summary.assignedPrinter
  const latestCommand = getLatestRelevantCommandLog(commandLogs, assignedPrinter)
  const latestCallback = getLatestRelevantCallbackLog(
    callbackLogs,
    assignedPrinter,
    latestCommand?.jobId
  )
  const jobWithCallback = getLatestRecord(jobs, (job) => Boolean(job.mqttCallback))
  const latestMishap = getLatestRecord(getRelevantRecords(mishaps, assignedPrinter))
  const printerFreshness = getPrinterFreshness(printerStatus)
  const operationalHealth = getOperationalPrinterHealth(printerStatus)
  const publishStatus = latestCommand
    ? latestCommand.mqttPublishOk === true
      ? 'Success'
      : latestCommand.mqttPublishOk === false
      ? 'Failed'
      : 'Unknown'
    : 'Missing'
  const presenceClientId = getPresenceClientId(printerConnection, latestCommand)
  const callbackReceived = Boolean(latestCallback || jobWithCallback?.mqttCallback)
  const callbackResult = latestCallback?.result || jobWithCallback?.mqttCallback?.result
  const callbackStage = latestCallback?.stage || jobWithCallback?.mqttCallback?.stage
  const callbackType = latestCallback?.type || jobWithCallback?.mqttCallback?.type
  const callbackError =
    latestCallback?.errorDetail ||
    latestCallback?.errorCode ||
    jobWithCallback?.mqttCallback?.errorDetail ||
    jobWithCallback?.mqttCallback?.message
  const callbackReceivedAt =
    latestCallback?.receivedAtMs ||
    latestCallback?.receivedAt ||
    jobWithCallback?.mqttCallback?.receivedAt ||
    jobWithCallback?.mqttCallback?.timestamp ||
    jobWithCallback?.mqttCallback?.completedAt
  const callbackMissingAfterPublish = latestCommand?.mqttPublishOk === true && !callbackReceived

  return (
    <SectionCard
      title='Delivery Evidence'
      helper='Evidence chain for backend MQTT publish, EMQX subscription, heartbeat, PrintStation callback, and mishaps.'
    >
      {isLoading && (
        <div className='alert alert-light-primary py-3 mb-4'>
          Loading delivery evidence from printer connection, command, callback, and mishap logs...
        </div>
      )}
      {Boolean(error) && (
        <div className='alert alert-light-warning py-3 mb-4'>
          Some delivery evidence could not load. Existing print history sections remain available.
        </div>
      )}
      <div className='row g-4'>
        <EvidenceCard
          title='Backend published MQTT command'
          icon='bi-send'
          tone={
            publishStatus === 'Success'
              ? 'success'
              : publishStatus === 'Failed'
              ? 'danger'
              : 'neutral'
          }
        >
          <EvidenceField label='MQTT publish' value={publishStatus} status={publishStatus} />
          <EvidenceField label='Job kind' value={getRecordJobKind(latestCommand) || EMPTY_TEXT} />
          <EvidenceField label='Topic' value={latestCommand?.topic} />
          <EvidenceField label='Job ID' value={latestCommand?.jobId} />
          <EvidenceField label='Trace ID' value={latestCommand?.traceId} />
          <EvidenceField
            label='Published time'
            value={latestCommand?.publishedAtMs || latestCommand?.publishedAt}
            isTimestamp
          />
          <EvidenceField
            label='Error'
            value={latestCommand?.mqttPublishError || latestCommand?.error}
          />
        </EvidenceCard>

        <EvidenceCard
          title='EMQX saw subscription'
          icon='bi-broadcast'
          tone={
            isObserverClientId(presenceClientId)
              ? 'warning'
              : getPresenceConnected(printerConnection, latestCommand)
              ? 'success'
              : 'neutral'
          }
        >
          <EvidenceField
            label='Connected'
            value={formatYesNoUnknown(getPresenceConnected(printerConnection, latestCommand))}
          />
          <EvidenceField
            label='Presence stale'
            value={formatYesNoUnknown(getPresenceStale(printerConnection, latestCommand))}
          />
          <EvidenceField
            label='Subscribed to prefetch'
            value={formatYesNoUnknown(
              getPresenceSubscribed(printerConnection, latestCommand, 'prefetch')
            )}
          />
          <EvidenceField
            label='Subscribed to print'
            value={formatYesNoUnknown(
              getPresenceSubscribed(printerConnection, latestCommand, 'print')
            )}
          />
          <EvidenceField label='Client ID' value={presenceClientId || EMPTY_TEXT} />
          <EvidenceField
            label='Last event time'
            value={getPresenceLastEventAt(printerConnection, latestCommand)}
            isTimestamp
          />
          {isObserverClientId(presenceClientId) && (
            <div className='alert alert-light-warning py-2 px-3 mt-2 mb-0 fs-7 fw-semibold'>
              Presence is from observer/test client, not confirmed real PrintStation.
            </div>
          )}
        </EvidenceCard>

        <EvidenceCard
          title='Heartbeat status'
          icon='bi-heart'
          tone={getStatusTone(operationalHealth)}
        >
          <EvidenceField
            label='Operational health'
            value={humanStatus(operationalHealth)}
            status={operationalHealth}
          />
          <EvidenceField label='Last ping' value={printerFreshness.lastSeenAtMs} isTimestamp />
          <EvidenceField label='Heartbeat age' value={formatAge(printerFreshness.ageMs)} />
          <EvidenceField label='Last reported status' value={printerStatus?.status} />
          <EvidenceField label='Paper' value={printerStatus?.paper} />
          <EvidenceField label='Ink' value={printerStatus?.ink} />
        </EvidenceCard>

        <EvidenceCard
          title='PrintStation callback'
          icon='bi-reply'
          tone={
            callbackReceived
              ? getStatusTone(String(callbackResult || '').toLowerCase())
              : callbackMissingAfterPublish
              ? 'warning'
              : 'neutral'
          }
        >
          <EvidenceField
            label='Callback received'
            value={callbackReceived ? 'Yes' : 'No'}
            status={callbackReceived ? 'completed' : 'missing'}
          />
          <EvidenceField label='Latest result' value={callbackResult} status={callbackResult} />
          <EvidenceField label='Stage' value={callbackStage} />
          <EvidenceField label='Type' value={callbackType} />
          <EvidenceField label='Received time' value={callbackReceivedAt} isTimestamp />
          <EvidenceField label='Error' value={callbackError} />
          {callbackMissingAfterPublish && (
            <div className='alert alert-light-warning py-2 px-3 mt-2 mb-0 fs-7 fw-semibold'>
              Backend published but callback is missing.
            </div>
          )}
        </EvidenceCard>

        <EvidenceCard
          title='Mishap recorded'
          icon='bi-exclamation-triangle'
          tone={
            latestMishap
              ? String(latestMishap.severity || '').toLowerCase() === 'critical'
                ? 'danger'
                : 'warning'
              : 'neutral'
          }
        >
          <EvidenceField label='Latest mishap type' value={latestMishap?.type} />
          <EvidenceField
            label='Severity'
            value={latestMishap?.severity}
            status={latestMishap?.severity}
          />
          <EvidenceField label='Message' value={latestMishap?.message} />
          <EvidenceField
            label='Created time'
            value={latestMishap?.createdAtMs || latestMishap?.createdAt}
            isTimestamp
          />
          <EvidenceField
            label='Resolved'
            value={latestMishap ? formatValue(latestMishap.resolved) : EMPTY_TEXT}
          />
        </EvidenceCard>
      </div>
    </SectionCard>
  )
}

const CodePrintableSection: FC<{codes: PrintHistoryDoc[]; emptyState: JSX.Element | null}> = ({
  codes,
  emptyState,
}) => {
  if (emptyState) {
    return <SectionCard title='Code & Printable'>{emptyState}</SectionCard>
  }

  if (codes.length === 1) {
    const code = codes[0]
    const codeId = getDocId(code, 'codeId')
    const url = getCodeUrl(code)

    return (
      <SectionCard
        title='Code & Printable'
        helper='Compact code and printable asset state for this content.'
      >
        <div className='row g-4'>
          {[
            ['Code', codeId],
            ['Type', getCodeType(code) || EMPTY_TEXT],
            ['Assigned printer', code.deviceId || EMPTY_TEXT],
            ['Printable', hasPrintable(code) ? 'Ready' : 'Not ready'],
            [
              'Used / allowed',
              `${formatValue(code.usedPrints)} / ${formatValue(code.allowedPrints)}`,
            ],
          ].map(([label, value]) => (
            <div className='col-md-6 col-xl' key={label}>
              <div className='border border-dashed rounded px-4 py-3 h-100'>
                <div className='text-muted fw-semibold fs-8 text-uppercase'>{label}</div>
                <div className='fw-bold text-gray-800 text-truncate mt-1' title={String(value)}>
                  {value}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className='mt-4'>
          <OpenButton url={url} label='Open printable' />
        </div>
      </SectionCard>
    )
  }

  return (
    <SectionCard title='Code & Printable' helper='Each code keeps its stamped assigned printer.'>
      <div className='table-responsive'>
        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
          <thead>
            <tr className='fw-bold text-muted'>
              <th>Code</th>
              <th>Type</th>
              <th>Printer</th>
              <th>Printable</th>
              <th>Used / Allowed</th>
              <th>Last Print</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => {
              const url = getCodeUrl(code)

              return (
                <tr key={code.id}>
                  <td className='fw-bold text-gray-800' style={{maxWidth: 180}}>
                    <span
                      className='d-inline-block text-truncate mw-100'
                      title={getDocId(code, 'codeId')}
                    >
                      {getDocId(code, 'codeId')}
                    </span>
                  </td>
                  <td>{getCodeType(code) || EMPTY_TEXT}</td>
                  <td className='text-gray-800'>{code.deviceId || EMPTY_TEXT}</td>
                  <td>
                    <span
                      className={`badge ${
                        hasPrintable(code) ? 'badge-light-success' : 'badge-light-warning'
                      }`}
                    >
                      {hasPrintable(code) ? 'Ready' : 'Not ready'}
                    </span>
                  </td>
                  <td>
                    {formatValue(code.usedPrints)} / {formatValue(code.allowedPrints)}
                  </td>
                  <td>
                    {formatTimestamp(
                      code.lastPrintedAt ||
                        code.lastPrintJob?.completedAt ||
                        code.lastPrintJob?.timestamp
                    )}
                  </td>
                  <td className='text-end'>
                    <OpenButton url={url} label='Open printable' />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  )
}

const ActivityBlock: FC<{items: Array<[string, any]>; title: string}> = ({items, title}) => (
  <div className='col-md-4'>
    <div className='border border-dashed rounded p-4 h-100'>
      <div className='fw-bold text-gray-800 mb-3'>{title}</div>
      <div className='d-flex flex-column gap-2'>
        {items.map(([label, value]) => (
          <div className='d-flex justify-content-between align-items-center gap-3' key={label}>
            <span className='text-muted fw-semibold'>{label}</span>
            <span
              className='fw-bold text-gray-800 text-end text-truncate'
              title={formatValue(value)}
              style={{maxWidth: 180}}
            >
              {formatValue(value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
)

const PrintActivitySummary: FC<{journey: PrintJourneyState}> = ({journey}) => {
  const copyModeLabel =
    journey.activity.copyMode === 'split-copy'
      ? 'Split-copy'
      : journey.activity.copyMode === 'native-copies'
      ? 'Native copies'
      : journey.activity.copyMode === 'single'
      ? 'Single copy'
      : 'Unknown'

  return (
    <SectionCard
      title='Print Activity Summary'
      helper='Operational counts without raw job payloads.'
    >
      <div className='row g-4 mb-5'>
        <ActivityBlock
          title='Prefetch'
          items={[
            ['Devices targeted', compactList(journey.activity.prefetchDevices)],
            ['Prefetch jobs', journey.activity.prefetchTotal],
            ['Confirmed', journey.activity.prefetchConfirmed],
            ['Pending', journey.activity.prefetchPending],
            ['Failed', journey.activity.prefetchFailed],
          ]}
        />
        <ActivityBlock
          title='Print'
          items={[
            ['Jobs published', journey.activity.printPublished],
            ['Device', journey.summary.assignedPrinter || EMPTY_TEXT],
            ['Completed', journey.activity.printCompleted],
            ['Failed', journey.activity.printFailed],
            ['Waiting confirmation', journey.activity.printWaitingCallback],
          ]}
        />
        <ActivityBlock
          title='Copies'
          items={[
            ['Requested', journey.summary.copiesRequested],
            ['Published', journey.summary.copiesPublished],
            ['Confirmed', journey.summary.copiesConfirmed],
            ['Mode', copyModeLabel],
          ]}
        />
      </div>
      <div className='d-flex flex-column gap-2'>
        <div className='alert alert-light-primary py-3 mb-0'>
          Prefetch was sent to {pluralize(journey.activity.prefetchDevices.length, 'device')}.{' '}
          {journey.activity.prefetchConfirmed > 0
            ? `${journey.activity.prefetchConfirmed} prefetch confirmation(s) received.`
            : 'No prefetch callbacks have been confirmed yet.'}
        </div>
        <div className='alert alert-light-primary py-3 mb-0'>
          {journey.activity.printPublished > 0
            ? `Print was published to ${journey.summary.assignedPrinter || 'the selected printer'}.`
            : 'No physical print job has been published yet.'}{' '}
          {journey.activity.printWaitingCallback > 0
            ? 'Printer confirmation is still pending.'
            : journey.activity.printCompleted > 0
            ? 'Printer confirmation has been received.'
            : ''}
        </div>
        <div className='alert alert-light-primary py-3 mb-0'>
          Copies requested: {journey.summary.copiesRequested}. Confirmed:{' '}
          {journey.summary.copiesConfirmed}.
        </div>
      </div>
    </SectionCard>
  )
}

const SummaryGroup: FC<{fields: Array<[string, any, boolean?]>; title: string}> = ({
  fields,
  title,
}) => (
  <div className='mb-6'>
    <div className='fw-bold text-gray-800 fs-6 mb-3'>{title}</div>
    <div className='row g-4'>
      {fields.map(([label, value, isTimestamp]) => (
        <SummaryField key={label} label={label} value={value} isTimestamp={isTimestamp} />
      ))}
    </div>
  </div>
)

const CodeTable: FC<{
  codes: PrintHistoryDoc[]
  emptyState: JSX.Element | null
  onFilterCode: (codeId: string) => void
}> = ({codes, emptyState, onFilterCode}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bold text-muted'>
            <th>codeId</th>
            <th>Type</th>
            <th>Staff</th>
            <th>Resolved Print Device</th>
            <th>Resolution Source</th>
            <th>Printable Ready</th>
            <th>Used / Allowed</th>
            <th>Printable Prefetch Key</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {codes.map((code) => {
            const codeId = getDocId(code, 'codeId')
            const url = getCodeUrl(code)

            return (
              <tr key={code.id}>
                <td className='fw-bold text-gray-800' style={{maxWidth: 220}}>
                  <CopyableValue value={codeId} />
                </td>
                <DataCell record={code} field='type' />
                <DataCell record={code} field='isStaffCode' />
                <DataCell record={code} field='deviceId' />
                <DataCell record={code} field='deviceIdResolutionSource' />
                <DataCell record={code} field='printableReady' />
                <td>
                  {formatValue(code.usedPrints)} / {formatValue(code.allowedPrints)}
                </td>
                <DataCell record={code} field='printablePrefetchKey' />
                <DataCell record={code} field='updatedAt' isTimestamp />
                <td>
                  <CopyButton value={codeId} label='Copy ID' />
                  <CopyButton value={url} label='Copy URL' />
                  <OpenButton url={url} />
                  <button
                    type='button'
                    className='btn btn-sm btn-light-info me-2 mb-2'
                    onClick={() => onFilterCode(codeId)}
                  >
                    Filter Jobs
                  </button>
                  <RawDetails
                    title='Secondary fields'
                    value={{
                      activationId: code.activationId,
                      contentUrl: code.contentUrl,
                      deviceIdResolutionMatchedKey: code.deviceIdResolutionMatchedKey,
                      lastPrintedAt: code.lastPrintedAt,
                      lastPrintJob: code.lastPrintJob,
                      memoryMakerId: code.memoryMakerId,
                      memoryMakerUid: code.memoryMakerUid,
                      memoryMakerUserDocId: code.memoryMakerUserDocId,
                      printableTemplateId: code.printableTemplateId,
                      printableUrl: code.printableUrl,
                      source: code.source,
                      templateId: code.templateId,
                    }}
                  />
                  <RawDetails title='Raw JSON' value={code} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const RenderJobsTable: FC<{jobs: PrintHistoryDoc[]; emptyState: JSX.Element | null}> = ({
  emptyState,
  jobs,
}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bold text-muted'>
            {[
              'jobId',
              'codeId',
              'status',
              'priority',
              'attempts',
              'printableTemplateId',
              'completedAt',
              'lastError',
              'Actions',
            ].map((heading) => (
              <th key={heading}>{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const jobId = getDocId(job, 'jobId')
            const url = job.printableUrl || job.contentUrl || ''

            return (
              <tr key={job.id}>
                <td className='fw-bold text-gray-800' style={{maxWidth: 220}}>
                  <CopyableValue value={jobId} />
                </td>
                <DataCell record={job} field='codeId' />
                <StatusCell record={job} />
                <DataCell record={job} field='priority' />
                <DataCell record={job} field='attempts' />
                <DataCell record={job} field='printableTemplateId' />
                <DataCell record={job} field='completedAt' isTimestamp />
                <DataCell record={job} field='lastError' />
                <td>
                  <OpenButton url={url} />
                  <CopyButton value={jobId} label='Copy ID' />
                  <RawDetails
                    title='Secondary fields'
                    value={{
                      contentId: job.contentId,
                      createdAt: job.createdAt,
                      printableUrl: job.printableUrl,
                      templateId: job.templateId,
                      type: job.type,
                      updatedAt: job.updatedAt,
                    }}
                  />
                  <RawDetails title='Raw JSON' value={job} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const PrefetchJobsTable: FC<{jobs: PrintHistoryDoc[]; emptyState: JSX.Element | null}> = ({
  emptyState,
  jobs,
}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bold text-muted'>
            {[
              'jobId',
              'codeId',
              'deviceId',
              'status',
              'prefetchKey',
              'callback result',
              'callback stage',
              'callback cached',
              'publishedAt',
              'completedAt',
              'Actions',
            ].map((heading) => (
              <th key={heading}>{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const jobId = getDocId(job, 'jobId')
            const url = getJobUrl(job)

            return (
              <tr key={job.id}>
                <td className='fw-bold text-gray-800' style={{maxWidth: 220}}>
                  <CopyableValue value={jobId} />
                </td>
                <DataCell record={job} field='codeId' />
                <DataCell record={job} field='deviceId' />
                <StatusCell record={job} />
                <DataCell record={job} field='prefetchKey' />
                <DataCell record={job} field='mqttCallback.result' />
                <DataCell record={job} field='mqttCallback.stage' />
                <DataCell record={job} field='mqttCallback.cached' />
                <DataCell record={job} field='timestamps.publishedAt' isTimestamp />
                <DataCell record={job} field='timestamps.completedAt' isTimestamp />
                <td>
                  <CopyButton value={jobId} label='Copy ID' />
                  <CopyButton value={url} label='Copy URL' />
                  <OpenButton url={url} />
                  <RawDetails title='mqttCallback' value={job.mqttCallback || {}} />
                  <RawDetails
                    title='Secondary fields'
                    value={{
                      contentId: job.contentId,
                      createdAt: job.timestamps?.createdAt,
                      failedAt: job.timestamps?.failedAt,
                      lastError: job.lastError,
                      printableTemplateId: job.printableTemplateId,
                      signedUrl: job.signedUrl,
                      storageUrl: job.storageUrl,
                      updatedAt: job.timestamps?.updatedAt,
                      url: job.url,
                    }}
                  />
                  <RawDetails title='Raw JSON' value={job} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const PrintJobsTable: FC<{jobs: PrintHistoryDoc[]; emptyState: JSX.Element | null}> = ({
  emptyState,
  jobs,
}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bold text-muted'>
            {[
              'jobId',
              'codeId',
              'deviceId',
              'status',
              'source',
              'copies',
              'copyIndex / copyTotal',
              'splitCopy',
              'callback result',
              'callback stage',
              'publishedAt',
              'completedAt',
              'failedAt',
              'Actions',
            ].map((heading) => (
              <th key={heading}>{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const jobId = getDocId(job, 'jobId')
            const url = getJobUrl(job)

            return (
              <tr key={job.id}>
                <td className='fw-bold text-gray-800' style={{maxWidth: 220}}>
                  <CopyableValue value={jobId} />
                </td>
                <DataCell record={job} field='codeId' />
                <DataCell record={job} field='deviceId' />
                <StatusCell record={job} />
                <DataCell record={job} field='source' />
                <DataCell record={job} field='copies' />
                <td>
                  {formatValue(job.copyIndex)} / {formatValue(job.copyTotal)}
                </td>
                <DataCell record={job} field='splitCopy' />
                <DataCell record={job} field='mqttCallback.result' />
                <DataCell record={job} field='mqttCallback.stage' />
                <DataCell record={job} field='timestamps.publishedAt' isTimestamp />
                <DataCell record={job} field='timestamps.completedAt' isTimestamp />
                <DataCell record={job} field='timestamps.failedAt' isTimestamp />
                <td>
                  <CopyButton value={jobId} label='Copy ID' />
                  <CopyButton value={url} label='Copy URL' />
                  <OpenButton url={url} />
                  <RawDetails title='MQTT payload' value={job.mqttPayload || {}} />
                  <RawDetails title='Callback' value={job.mqttCallback || {}} />
                  <RawDetails
                    title='Secondary fields'
                    value={{
                      contentId: job.contentId,
                      createdAt: job.timestamps?.createdAt,
                      deviceIdResolutionSource: job.deviceIdResolutionSource,
                      error: job.error || job.errorDetail || job.lastError,
                      memoryMakerId: job.memoryMakerId,
                      memoryMakerUid: job.memoryMakerUid,
                      parentPrintJobId: job.parentPrintJobId,
                      quotaAllowed: job.quotaAllowed,
                      quotaUsed: job.quotaUsed,
                      requestedCopies: job.requestedCopies,
                      updatedAt: job.timestamps?.updatedAt,
                    }}
                  />
                  <RawDetails title='Raw JSON' value={job} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const PrinterStatusCards: FC<{statuses: PrintHistoryDoc[]; emptyState: JSX.Element | null}> = ({
  emptyState,
  statuses,
}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='row g-4'>
      {statuses.map((printer) => {
        const freshness = getPrinterFreshness(printer)
        const operationalHealth = getOperationalPrinterHealth(printer)

        return (
          <div className='col-md-6 col-xl-4' key={printer.id}>
            <div className='border border-dashed rounded p-4 h-100'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='fw-bold text-gray-800 text-break'>
                  {printer.deviceId || printer.id}
                </div>
                <span
                  className={`badge ${
                    printer.missing ? 'badge-light-warning' : getStatusBadgeClass(operationalHealth)
                  }`}
                >
                  {printer.missing ? 'Missing' : humanStatus(operationalHealth)}
                </span>
              </div>
              {printer.missing ? (
                <div className='text-muted fw-semibold'>No status found for this device</div>
              ) : (
                <div className='row g-3'>
                  {[
                    ['Operational', humanStatus(operationalHealth)],
                    ['Freshness', humanStatus(freshness.state)],
                    ['Reported isOnline', printer.isOnline],
                    ['Reported status', printer.status],
                    ['Health', printer.healthScore],
                    ['Paper', printer.paper],
                    ['Ink', printer.ink],
                    ['Queued', printer.queuedJobs],
                    ['Cached', printer.cachedAssets],
                    ['Last Ping', formatTimestamp(freshness.lastSeenAtMs)],
                    ['Heartbeat Age', formatAge(freshness.ageMs)],
                    ['Expires At', formatTimestamp(freshness.expiresAtMs)],
                    ['Updated', formatTimestamp(printer.updatedAt)],
                  ].map(([label, value]) => (
                    <div className='col-6' key={String(label)}>
                      <div className='text-muted fs-8'>{label}</div>
                      <div className='fw-semibold text-gray-800 text-break'>
                        {formatValue(value)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const PrinterConnectionCards: FC<{
  connections: PrintHistoryDoc[]
  emptyState: JSX.Element | null
}> = ({connections, emptyState}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='row g-4'>
      {connections.map((connection) => (
        <div className='col-md-6 col-xl-4' key={connection.id}>
          <div className='border border-dashed rounded p-4 h-100'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div className='fw-bold text-gray-800 text-break'>
                {connection.deviceId || connection.id}
              </div>
              <span
                className={`badge ${
                  connection.missing
                    ? 'badge-light-warning'
                    : getStatusBadgeClass(connection.connected ? 'online' : 'offline')
                }`}
              >
                {connection.missing
                  ? 'Missing'
                  : connection.connected
                  ? 'Connected'
                  : 'Disconnected'}
              </span>
            </div>
            {connection.missing ? (
              <div className='text-muted fw-semibold'>
                No printerConnections record found for this device
              </div>
            ) : (
              <>
                <div className='row g-3'>
                  {[
                    ['Client ID', connection.clientId],
                    [
                      'Observer/test client',
                      isObserverClientId(connection.clientId) ? 'Yes' : 'No',
                    ],
                    ['Last event', connection.lastEvent],
                    [
                      'Last event time',
                      formatTimestamp(connection.lastEventAtMs || connection.updatedAt),
                    ],
                    ['Username', connection.username],
                    [
                      'Topics',
                      Array.isArray(connection.subscribedTopics)
                        ? connection.subscribedTopics.join(', ')
                        : connection.subscribedTopics,
                    ],
                  ].map(([label, value]) => (
                    <div className='col-6' key={String(label)}>
                      <div className='text-muted fs-8'>{label}</div>
                      <div className='fw-semibold text-gray-800 text-break'>
                        {formatValue(value)}
                      </div>
                    </div>
                  ))}
                </div>
                {isObserverClientId(connection.clientId) && (
                  <div className='alert alert-light-warning py-2 px-3 mt-3 mb-0 fs-7 fw-semibold'>
                    Presence is from observer/test client, not confirmed real PrintStation.
                  </div>
                )}
                <RawDetails title='Raw JSON' value={connection} />
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const CommandLogsTable: FC<{logs: PrintHistoryDoc[]; emptyState: JSX.Element | null}> = ({
  emptyState,
  logs,
}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bold text-muted'>
            {[
              'jobId',
              'jobKind',
              'deviceId',
              'mqttPublishOk',
              'topic',
              'traceId',
              'publishedAtMs',
              'callbackDueAtMs',
              'Actions',
            ].map((heading) => (
              <th key={heading}>{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id}>
              <DataCell record={log} field='jobId' />
              <DataCell record={log} field='jobKind' />
              <DataCell record={log} field='deviceId' />
              <DataCell record={log} field='mqttPublishOk' />
              <DataCell record={log} field='topic' />
              <DataCell record={log} field='traceId' />
              <DataCell record={log} field='publishedAtMs' isTimestamp />
              <DataCell record={log} field='callbackDueAtMs' isTimestamp />
              <td>
                <RawDetails title='Presence evidence' value={log.presenceEvidence || {}} />
                <RawDetails title='Payload' value={log.payload || {}} />
                <RawDetails title='Raw JSON' value={log} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const CallbackLogsTable: FC<{logs: PrintHistoryDoc[]; emptyState: JSX.Element | null}> = ({
  emptyState,
  logs,
}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bold text-muted'>
            {[
              'jobId',
              'stage',
              'result',
              'type',
              'deviceId',
              'receivedAtMs',
              'errorCode',
              'errorDetail',
              'Actions',
            ].map((heading) => (
              <th key={heading}>{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id}>
              <DataCell record={log} field='jobId' />
              <DataCell record={log} field='stage' />
              <StatusCell record={log} field='result' />
              <DataCell record={log} field='type' />
              <DataCell record={log} field='deviceId' />
              <DataCell record={log} field='receivedAtMs' isTimestamp />
              <DataCell record={log} field='errorCode' />
              <DataCell record={log} field='errorDetail' />
              <td>
                <RawDetails title='Raw payload' value={log.rawPayload || {}} />
                <RawDetails title='Raw JSON' value={log} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const MishapsTable: FC<{mishaps: PrintHistoryDoc[]; emptyState: JSX.Element | null}> = ({
  emptyState,
  mishaps,
}) => {
  if (emptyState) {
    return emptyState
  }

  return (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bold text-muted'>
            {[
              'type',
              'severity',
              'jobId',
              'deviceId',
              'message',
              'createdAtMs',
              'resolved',
              'Actions',
            ].map((heading) => (
              <th key={heading}>{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mishaps.map((mishap) => (
            <tr key={mishap.id}>
              <DataCell record={mishap} field='type' />
              <StatusCell record={mishap} field='severity' />
              <DataCell record={mishap} field='jobId' />
              <DataCell record={mishap} field='deviceId' />
              <DataCell record={mishap} field='message' />
              <DataCell record={mishap} field='createdAtMs' isTimestamp />
              <DataCell record={mishap} field='resolved' />
              <td>
                <RawDetails title='Evidence' value={mishap.evidence || {}} />
                <RawDetails title='Raw JSON' value={mishap} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const ContentPrintHistory: FC<Props> = ({
  content,
  mode = 'all',
  enabled = true,
  onOpenJson,
}) => {
  const contentId = content.id || ''
  const [codeFilter, setCodeFilter] = useState('')
  const queriesEnabled = Boolean(contentId) && enabled
  const showPrintFlow = mode === 'printFlow' || mode === 'all'
  const showJson = mode === 'json' || mode === 'all'

  const rawContentQuery = useQuery(
    ['content-print-history', 'content', contentId],
    () => getRawContentDoc(contentId),
    getLivePrintHistoryQueryOptions(queriesEnabled)
  )

  const codesQuery = useQuery(
    ['content-print-history', 'codes', contentId],
    () => getCollectionDocsByContentId('codes', contentId, 'codeId'),
    getLivePrintHistoryQueryOptions(queriesEnabled)
  )

  const codeIds = useMemo(
    () => (codesQuery.data || []).map((code) => getDocId(code, 'codeId')).filter(Boolean),
    [codesQuery.data]
  )

  const renderJobsQuery = useQuery(
    ['content-print-history', 'renderJobs', contentId, codeIds.join('|')],
    () => getRenderJobs(contentId, codeIds),
    getLivePrintHistoryQueryOptions(queriesEnabled)
  )

  const printJobsQuery = useQuery(
    ['content-print-history', 'printJobs', contentId],
    () => getCollectionDocsByContentId('printJobs', contentId, 'jobId'),
    getLivePrintHistoryQueryOptions(queriesEnabled)
  )

  const printerCommandLogsQuery = useQuery(
    ['content-print-history', 'printerCommandLogs', contentId],
    () => getEvidenceDocsByContentId('printerCommandLogs', contentId, 'logId'),
    getLivePrintHistoryQueryOptions(queriesEnabled)
  )

  const printerCallbackLogsQuery = useQuery(
    ['content-print-history', 'printerCallbackLogs', contentId],
    () => getEvidenceDocsByContentId('printerCallbackLogs', contentId, 'logId'),
    getLivePrintHistoryQueryOptions(queriesEnabled)
  )

  const printerMishapsQuery = useQuery(
    ['content-print-history', 'printerMishaps', contentId],
    () => getEvidenceDocsByContentId('printerMishaps', contentId, 'mishapId'),
    getLivePrintHistoryQueryOptions(queriesEnabled)
  )

  const rawContent: PrintHistoryDoc | null = rawContentQuery.data || null
  const codes = codesQuery.data || []
  const renderJobs = renderJobsQuery.data || []
  const allPrintJobs = printJobsQuery.data || []
  const printerCommandLogs = printerCommandLogsQuery.data || []
  const printerCallbackLogs = printerCallbackLogsQuery.data || []
  const printerMishaps = printerMishapsQuery.data || []
  const prefetchJobs = allPrintJobs.filter(isPrefetchJob)
  const printJobs = allPrintJobs.filter(isPrintJob)
  const filteredPrefetchJobs = codeFilter
    ? prefetchJobs.filter((job) => job.codeId === codeFilter)
    : prefetchJobs
  const filteredPrintJobs = codeFilter
    ? printJobs.filter((job) => job.codeId === codeFilter)
    : printJobs

  const deviceIds = useMemo(() => {
    const devices = [
      rawContent?.deviceId,
      content.deviceId,
      ...codes.map((code) => code.deviceId),
      ...prefetchJobs.map((job) => job.deviceId),
      ...printJobs.map((job) => job.deviceId),
      ...printerCommandLogs.map((log) => log.deviceId),
      ...printerCallbackLogs.map((log) => log.deviceId),
      ...printerMishaps.map((mishap) => mishap.deviceId),
    ]
      .filter(
        (deviceId): deviceId is string => typeof deviceId === 'string' && deviceId.trim().length > 0
      )
      .map((deviceId) => deviceId.trim())

    return Array.from(new Set(devices))
  }, [
    codes,
    content.deviceId,
    prefetchJobs,
    printJobs,
    printerCallbackLogs,
    printerCommandLogs,
    printerMishaps,
    rawContent?.deviceId,
  ])

  const printerStatusesQuery = useQuery(
    ['content-print-history', 'printerStatus', deviceIds.join('|')],
    () => getPrinterStatuses(deviceIds),
    getLivePrintHistoryQueryOptions(queriesEnabled && deviceIds.length > 0)
  )

  const printerConnectionsQuery = useQuery(
    ['content-print-history', 'printerConnections', deviceIds.join('|')],
    () => getPrinterConnections(deviceIds),
    getLivePrintHistoryQueryOptions(queriesEnabled && deviceIds.length > 0)
  )

  const printerStatuses = printerStatusesQuery.data || []
  const printerConnections = printerConnectionsQuery.data || []

  const observerLoading =
    rawContentQuery.isLoading ||
    codesQuery.isLoading ||
    renderJobsQuery.isLoading ||
    printJobsQuery.isLoading ||
    printerStatusesQuery.isLoading
  const observerError =
    rawContentQuery.error ||
    codesQuery.error ||
    renderJobsQuery.error ||
    printJobsQuery.error ||
    printerStatusesQuery.error

  const summary = useMemo(() => {
    const actualPrintDevices = Array.from(
      new Set(printJobs.map((job) => job.deviceId).filter(Boolean))
    )
    const completedPrintJobs = printJobs.filter(
      (job) => getStatusBucket(job.status) === 'completed'
    ).length
    const failedPrintJobs = printJobs.filter(
      (job) => getStatusBucket(job.status) === 'failed'
    ).length
    const pendingPrintJobs = printJobs.filter(
      (job) => getStatusBucket(job.status) === 'pending'
    ).length
    const publishedPrintJobs = printJobs.filter(
      (job) => getStatusBucket(job.status) === 'published'
    ).length
    const prefetchCompleted = prefetchJobs.filter(
      (job) => getStatusBucket(job.status) === 'completed'
    ).length
    const prefetchFailed = prefetchJobs.filter(
      (job) => getStatusBucket(job.status) === 'failed'
    ).length
    const prefetchPending = prefetchJobs.filter(
      (job) => getStatusBucket(job.status) === 'pending'
    ).length
    const prefetchPublished = prefetchJobs.filter(
      (job) => getStatusBucket(job.status) === 'published'
    ).length
    const requestedCopies = printJobs.reduce(
      (total, job) =>
        total + Number(job.requestedCopies || job.copies || (job.splitCopy ? 1 : 0) || 0),
      0
    )
    const completedCopies = printJobs
      .filter((job) => getStatusBucket(job.status) === 'completed')
      .reduce((total, job) => total + Number(job.copies || 1), 0)

    return {
      actualPrintDevices,
      completedCopies,
      completedPrintJobs,
      consumerCodes: codes.filter((code) => !code.isStaffCode).length,
      devicesInvolved: deviceIds,
      failedPrintJobs,
      pendingPrintJobs,
      prefetchCompleted,
      prefetchFailed,
      prefetchPending,
      prefetchPublished,
      publishedPrintJobs,
      renderCompleted: renderJobs.filter((job) => getStatusBucket(job.status) === 'completed')
        .length,
      renderFailed: renderJobs.filter((job) => getStatusBucket(job.status) === 'failed').length,
      renderPending: renderJobs.filter((job) => getStatusBucket(job.status) === 'pending').length,
      requestedCopies,
      resolvedPrintDevice:
        actualPrintDevices[0] || codes.find((code) => code.deviceId)?.deviceId || '',
      resolvedPrintDevices: Array.from(new Set(codes.map((code) => code.deviceId).filter(Boolean))),
      staffCodes: codes.filter((code) => code.isStaffCode).length,
      totalCodes: codes.length,
      totalPrefetchJobs: prefetchJobs.length,
      totalPrintJobs: printJobs.length,
      totalRenderJobs: renderJobs.length,
    }
  }, [codes, deviceIds, prefetchJobs, printJobs, renderJobs])

  const autoPrintExplanation = useMemo(() => {
    if (rawContentQuery.isLoading) {
      return 'Checking auto-print state...'
    }

    if (rawContent?.autoPrint === true) {
      return 'Auto-print requested'
    }

    const reasons: string[] = []
    const emails =
      rawContent && Array.isArray(rawContent.emails) ? rawContent.emails : content.emails || []
    const qrCodes =
      rawContent && Array.isArray(rawContent.qrCodes) ? rawContent.qrCodes : content.qrCodes || []
    const priorPrintCount = Number(
      rawContent?.prints ||
        rawContent?.printCount ||
        rawContent?.manualPrintCount ||
        content.prints ||
        0
    )
    const activationAutoPrint = content.activations?.[0]?.data?.autoPrint

    if (emails.length > 0) {
      reasons.push('emails present')
    }

    if (qrCodes.length > 0) {
      reasons.push('QR codes present')
    }

    if (priorPrintCount > 0) {
      reasons.push('prior/manual print count present')
    }

    if (activationAutoPrint === false) {
      reasons.push('activation autoPrint disabled')
    }

    if (!rawContent?.staffCodeId) {
      reasons.push('missing staff code')
    }

    if (codes.length > 0 && !codes.some((code) => code.printableReady || code.printableUrl)) {
      reasons.push('printable not ready')
    }

    return reasons.length > 0
      ? `Not auto-printing: ${reasons.join(', ')}`
      : 'Not enough data to explain auto-print state'
  }, [
    codes,
    content.activations,
    content.emails,
    content.prints,
    content.qrCodes,
    rawContent,
    rawContentQuery.isLoading,
  ])

  const contentSessionFields = [
    ['contentId', contentId],
    ['session', rawContent?.session || content.session],
    ['sessionNumber', rawContent?.sessionNumber || content.sessionNumber],
    ['event / activationId', rawContent?.activationId || rawContent?.event],
    ['creator', rawContent?.creator],
    ['memoryMakerUid', rawContent?.memoryMakerUid],
    ['staffCodeId', rawContent?.staffCodeId],
    ['content/source device hint', rawContent?.deviceId || content.deviceId],
  ] as Array<[string, any, boolean?]>

  const autoPrintFields = [
    ['autoPrint', rawContent?.autoPrint],
    ['autoPrintCopies', rawContent?.autoPrintCopies],
    ['autoPrintExpectedCopies', rawContent?.autoPrintExpectedCopies],
    ['autoPrintRequestedCopies', rawContent?.autoPrintRequestedCopies],
    ['autoPrintPublishedCopies', rawContent?.autoPrintPublishedCopies],
    ['autoPrintConfirmedCopies', rawContent?.autoPrintConfirmedCopies],
    ['autoPrintCopiesPrinted', rawContent?.autoPrintCopiesPrinted],
    ['autoPrintCopyConfirmationStatus', rawContent?.autoPrintCopyConfirmationStatus],
    ['autoPrintPrintCompletion', rawContent?.autoPrintPrintCompletion],
    ['autoPrintCallbackCopiesExplicit', rawContent?.autoPrintCallbackCopiesExplicit],
    ['autoPrintCallbackCopiesSource', rawContent?.autoPrintCallbackCopiesSource],
    ['autoPrintStatus', rawContent?.autoPrintStatus],
    ['autoPrintSource', rawContent?.autoPrintSource],
    ['autoPrintStrategy', rawContent?.autoPrintStrategy],
    ['autoPrintRequestId', rawContent?.autoPrintRequestId],
    ['autoPrintRequestedAt', rawContent?.autoPrintRequestedAt, true],
    ['autoPrintCodeId', rawContent?.autoPrintCodeId],
    ['autoPrintParentPrintJobId', rawContent?.autoPrintParentPrintJobId],
    ['autoPrintChildPrintJobIds', rawContent?.autoPrintChildPrintJobIds],
    ['autoPrintPrefetchJobId', rawContent?.autoPrintPrefetchJobId],
    ['autoPrintPrefetchStatus', rawContent?.autoPrintPrefetchStatus],
    ['autoPrintPrintJobId', rawContent?.autoPrintPrintJobId],
    ['autoPrintPublishedAt', rawContent?.autoPrintPublishedAt, true],
    ['autoPrintPartialAt', rawContent?.autoPrintPartialAt, true],
    ['autoPrintLastCallbackAt', rawContent?.autoPrintLastCallbackAt, true],
    ['autoPrintLastCallbackJobId', rawContent?.autoPrintLastCallbackJobId],
    ['autoPrintLastCallbackResult', rawContent?.autoPrintLastCallbackResult],
    ['autoPrintLastCopiesPrinted', rawContent?.autoPrintLastCopiesPrinted],
    ['autoPrintLastCheckedAt', rawContent?.autoPrintLastCheckedAt, true],
    ['autoPrintLockedAt', rawContent?.autoPrintLockedAt, true],
    ['lastPrintExpectedCopies', rawContent?.lastPrintExpectedCopies],
    ['lastPrintRequestedCopies', rawContent?.lastPrintRequestedCopies],
    ['lastPrintConfirmedCopies', rawContent?.lastPrintConfirmedCopies],
    ['lastPrintCopiesPrinted', rawContent?.lastPrintCopiesPrinted],
    ['lastPrintCopyConfirmationStatus', rawContent?.lastPrintCopyConfirmationStatus],
    ['lastPrintCompletion', rawContent?.lastPrintCompletion],
    ['lastPrintCallbackCopiesExplicit', rawContent?.lastPrintCallbackCopiesExplicit],
    ['lastPrintCallbackCopiesSource', rawContent?.lastPrintCallbackCopiesSource],
    ['lastPrintCallbackAt', rawContent?.lastPrintCallbackAt, true],
    ['lastPrintJob', rawContent?.lastPrintJob],
    ['lastPrintedAt', rawContent?.lastPrintedAt, true],
  ] as Array<[string, any, boolean?]>

  const eligibilityFields = [
    [
      'emails.length',
      rawContent && Array.isArray(rawContent.emails)
        ? rawContent.emails.length
        : content.emails?.length,
    ],
    [
      'qrCodes.length',
      rawContent && Array.isArray(rawContent.qrCodes)
        ? rawContent.qrCodes.length
        : content.qrCodes?.length,
    ],
    [
      'mails / prior print count',
      rawContent?.mails ?? rawContent?.prints ?? content.mails ?? content.prints,
    ],
  ] as Array<[string, any, boolean?]>

  const deviceFields = [
    ['devices involved', summary.devicesInvolved.join(', ')],
    ['resolved print devices', summary.resolvedPrintDevices.join(', ')],
    ['actual print devices', summary.actualPrintDevices.join(', ')],
  ] as Array<[string, any, boolean?]>

  const jsonBody = (
    <>
      <SectionCard title='Content Metadata'>
        <SummaryGroup title='Content & Session' fields={contentSessionFields} />
        <SummaryGroup title='Eligibility Signals' fields={eligibilityFields} />
        <SummaryGroup title='Devices' fields={deviceFields} />
      </SectionCard>

      <SectionCard title='Auto-print Metadata'>
        <div className='alert alert-light-warning py-3 mb-6'>
          <div className='fw-bold'>{autoPrintExplanation}</div>
        </div>
        <SummaryGroup title='Auto-print Fields' fields={autoPrintFields} />
      </SectionCard>

      <SectionCard
        title='Codes Details'
        helper='Resolved Print Device is the stamped route for the code. Existing codes keep this route even if activation settings change later.'
      >
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3'>
          {codeFilter && (
            <button
              type='button'
              className='btn btn-sm btn-light-danger'
              onClick={() => setCodeFilter('')}
            >
              Clear job filter: {codeFilter}
            </button>
          )}
        </div>
        <div className='text-muted fs-7 mb-3'>
          prn1 and PRN001 are different device IDs unless a station explicitly treats them as
          aliases.
        </div>
        <CodeTable
          codes={codes}
          onFilterCode={setCodeFilter}
          emptyState={
            <SectionState
              emptyText='No codes found for this content.'
              error={codesQuery.error}
              isEmpty={!codesQuery.isLoading && codes.length === 0}
              isLoading={codesQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard
        title='Render Jobs Details'
        helper='Composite/render jobs create printable assets for linked codes.'
      >
        <RenderJobsTable
          jobs={renderJobs}
          emptyState={
            <SectionState
              emptyText='No render jobs found for this content.'
              error={renderJobsQuery.error}
              isEmpty={!renderJobsQuery.isLoading && renderJobs.length === 0}
              isLoading={renderJobsQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard
        title='Prefetch Jobs Details'
        helper='Prefetch may appear for multiple devices because assets are cached on all enabled printers. Prefetch does not mean a physical print happened.'
      >
        <PrefetchJobsTable
          jobs={filteredPrefetchJobs}
          emptyState={
            <SectionState
              emptyText='No prefetch jobs found for this content.'
              error={printJobsQuery.error}
              isEmpty={!printJobsQuery.isLoading && filteredPrefetchJobs.length === 0}
              isLoading={printJobsQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard
        title='Print Jobs Details'
        helper='Print jobs are the actual physical print requests. If split-copy is active, multiple one-copy print jobs are expected.'
      >
        <PrintJobsTable
          jobs={filteredPrintJobs}
          emptyState={
            <SectionState
              emptyText='No print jobs found for this content.'
              error={printJobsQuery.error}
              isEmpty={!printJobsQuery.isLoading && filteredPrintJobs.length === 0}
              isLoading={printJobsQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard
        title='Printer Status Details'
        helper='prn1 and PRN001 are different device IDs unless a station explicitly treats them as aliases.'
      >
        <PrinterStatusCards
          statuses={printerStatuses}
          emptyState={
            <SectionState
              emptyText='No printer status found for involved devices.'
              error={printerStatusesQuery.error}
              isEmpty={!printerStatusesQuery.isLoading && printerStatuses.length === 0}
              isLoading={printerStatusesQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard
        title='Printer Connection Details'
        helper='Latest EMQX subscription/presence evidence per involved device.'
      >
        <PrinterConnectionCards
          connections={printerConnections}
          emptyState={
            <SectionState
              emptyText='No printer connection evidence found for involved devices.'
              error={printerConnectionsQuery.error}
              isEmpty={!printerConnectionsQuery.isLoading && printerConnections.length === 0}
              isLoading={printerConnectionsQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard
        title='Command Logs Details'
        helper='Backend MQTT publish attempts for prefetch and print jobs.'
      >
        <CommandLogsTable
          logs={printerCommandLogs}
          emptyState={
            <SectionState
              emptyText='No printer command logs found for this content.'
              error={printerCommandLogsQuery.error}
              isEmpty={!printerCommandLogsQuery.isLoading && printerCommandLogs.length === 0}
              isLoading={printerCommandLogsQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard
        title='Callback Logs Details'
        helper='PrintStation callbacks received by the backend.'
      >
        <CallbackLogsTable
          logs={printerCallbackLogs}
          emptyState={
            <SectionState
              emptyText='No printer callback logs found for this content.'
              error={printerCallbackLogsQuery.error}
              isEmpty={!printerCallbackLogsQuery.isLoading && printerCallbackLogs.length === 0}
              isLoading={printerCallbackLogsQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard title='Mishaps Details' helper='Backend-detected callback timeouts and failures.'>
        <MishapsTable
          mishaps={printerMishaps}
          emptyState={
            <SectionState
              emptyText='No printer mishaps found for this content.'
              error={printerMishapsQuery.error}
              isEmpty={!printerMishapsQuery.isLoading && printerMishaps.length === 0}
              isLoading={printerMishapsQuery.isLoading}
            />
          }
        />
      </SectionCard>

      <SectionCard
        title='Raw Firestore JSON'
        helper='Raw Firestore payloads will appear here when related documents are loaded.'
      >
        <RawDetails title='Selected _contents doc JSON' value={rawContent || {}} />
        <RawDetails title='Linked codes JSON' value={codes} />
        <RawDetails title='Linked renderJobs JSON' value={renderJobs} />
        <RawDetails title='Linked printJobs JSON' value={allPrintJobs} />
        <RawDetails title='Linked printerStatus JSON' value={printerStatuses} />
        <RawDetails title='Linked printerConnections JSON' value={printerConnections} />
        <RawDetails title='Linked printerCommandLogs JSON' value={printerCommandLogs} />
        <RawDetails title='Linked printerCallbackLogs JSON' value={printerCallbackLogs} />
        <RawDetails title='Linked printerMishaps JSON' value={printerMishaps} />
      </SectionCard>
    </>
  )

  return (
    <div className='fv-row mb-7'>
      {mode === 'all' && (
        <>
          <div className='separator separator-dashed my-8'></div>
          <div className='card-header border-0 px-0 mb-5'>
            <div>
              <h3 className='card-title fw-bold text-dark fs-4 mb-2'>Print History</h3>
              <div className='text-muted fw-semibold fs-7'>
                Content-level print observer. Existing codes keep their stamped printer route;
                activation changes affect newly generated codes.
              </div>
            </div>
          </div>
        </>
      )}

      {showPrintFlow &&
        (observerLoading ? (
          <div className='border border-dashed rounded p-5 mb-8'>
            <div className='text-muted fw-semibold'>Loading print journey...</div>
          </div>
        ) : (
          <>
            {observerError && (
              <div className='alert alert-light-warning py-3 mb-6'>
                Some related print history data could not load. The observer is showing the data that
                is available.
              </div>
            )}

            <PrintFlowPanel
              content={rawContent || (content as any)}
              codes={codes}
              renderJobs={renderJobs}
              printJobs={allPrintJobs}
              printerStatuses={printerStatuses}
              printerConnections={printerConnections}
              callbackLogs={printerCallbackLogs}
              commandLogs={printerCommandLogs}
              mishaps={printerMishaps}
              emails={
                Array.isArray(rawContent?.emails)
                  ? (rawContent?.emails as string[])
                  : content.emails || []
              }
              onOpenJson={() => onOpenJson?.()}
            />
          </>
        ))}

      {showJson && mode === 'json' && <div className='pt-2'>{jsonBody}</div>}

      {showJson && mode === 'all' && (
        <details className='border border-dashed rounded p-5 mb-8'>
          <summary className='fw-bold text-gray-800 cursor-pointer'>
            Technical Details
            <span className='text-muted fw-semibold fs-7 ms-3'>
              Raw metadata, job payloads, and Firestore JSON are collapsed by default.
            </span>
          </summary>
          <div className='pt-5'>{jsonBody}</div>
        </details>
      )}
    </div>
  )
}


export {ContentPrintHistory}
